/**
 * PostProcessingPipeline — wraps THREE.PostProcessing (TSL-based).
 *
 * Supports 25 post-processing effects via Three.js TSL nodes:
 *   AO, SSGI, SSR, Denoise, God Rays, DOF, Motion Blur, Bloom, Anamorphic,
 *   After Image, Outline, Film, Dot Screen, RGB Shift, Chromatic Aberration,
 *   Sepia, Grayscale, LUT 3D, Sobel, Gaussian Blur, Box Blur, Hash Blur,
 *   Transition, FXAA, SMAA, TRAA.
 *
 * Works on both WebGPU and WebGL2 backends.
 * Provides named pass management with enable/disable controls.
 */

import * as THREE from 'three/webgpu';
import { pass, perspectiveDepthToViewZ, orthographicDepthToViewZ, uniform, float, mix, vec2, vec4, convertToTexture, texture3D, renderOutput, clamp, toneMappingExposure } from 'three/tsl';
import { createLumaGrainNode } from './post/lumaGrain.js';
import { applyPurkinje, createPurkinjeUniforms, type PurkinjeUniforms } from './color/purkinje.js';
import { SSR_REPLACE } from './post/ssrComposite.js';
import { skyMask, geometryMask } from './depth/skyMask.js';
import { installMrtOverlayBlending, mrtSurfaceOutput } from './post/mrtOverlay.js';
import { createEASUNode, createRCASNode, type EASUNodeResult, type RCASNodeResult } from './upscale/fsr/FSRNode.js';
import { FSR_PRESETS, type FSRQuality } from './upscale/fsr/FSRConfig.js';
import { Logger } from '../core/Logger.js';
import { createGodRaysNode, type GodRaysConfig, type GodRaysNodeResult } from './gi/GodRaysPass.js';
import type { LensFlareNodeResult } from './lensflares/LensFlareNode.js';
import { createLensFlareConfig, type LensFlareConfig } from './lensflares/LensFlareConfig.js';
import {
  createContactShadowsNode,
  getPrimaryLightState,
  type ContactShadowsUniforms,
} from './shadows/ContactShadowsNode.js';
import type { ContactShadowsConfig } from './shadows/ContactShadowsConfig.js';
import {
  createVignetteNode, createLensDistortionNode, createColorGradeNode, createSharpenNode,
  type VignetteConfig, type VignetteUniforms,
  type LensDistortionConfig, type LensDistortionUniforms,
  type ColorGradeConfig, type ColorGradeUniforms,
  type SharpenConfig, type SharpenUniforms,
} from './post/ScreenEffects.js';
import {
  createAerialCompositeNode,
  type AerialCompositeConfig, type AerialCompositeUniforms,
} from './sky/lut/aerialComposite.js';
import {
  createVolumetricFogNode,
  type VolumetricFogPassConfig, type VolumetricFogUniforms,
} from './fog/VolumetricFogPass.js';
import {
  createCapsuleShadowsNode,
  type CapsuleShadowsPassConfig, type CapsuleShadowsUniforms,
} from './shadows/capsule/CapsuleShadowsNode.js';

// ── Existing Effect Configs ─────────────────────────────────────────────────

export interface BloomConfig {
  threshold?: number;
  strength?: number;
  radius?: number;
  /**
   * 'mip' (default): three's BloomNode Gaussian mip chain. 'kawase': the
   * dual-filter bloom (Bjørge 2015 — two down + two up passes, a fraction of
   * the texture reads; the low-tier bloom, shader wave stage 4).
   */
  mode?: 'mip' | 'kawase';
  /** Kawase down/up pass pairs (default 2; 5 for the energy-conserving composite). */
  passes?: number;
  /**
   * 'add' (default, the legacy glow): scene + bloom·strength over a luminance
   * threshold. 'lerp' (lighting agent 2026-09-25, Atlas realism §8 R4 /
   * §10 R5): THRESHOLD-LESS and ENERGY-CONSERVING — out = mix(scene, blur,
   * strength) with a normalised multi-level Kawase pyramid (a sharp core and
   * wide tails, CoD:AW's veil): every bright region veils a little, nothing
   * adds light. Kawase only; strength 0.03–0.05.
   */
  composite?: 'add' | 'lerp';
}

export interface AOConfig {
  intensity?: number;
  radius?: number;
  samples?: number;
  /** GTAO render-target scale (0.5 = half resolution; AO is blurred anyway). Default 1. */
  resolutionScale?: number;
}

export interface FXAAConfig {
  enabled?: boolean;
  /** AA quality 2|4|8|16|32 → log2(q) iterated passes (see the AA section). */
  quality?: number;
}

export interface SSRConfig {
  intensity?: number;
  maxDistance?: number;
  thickness?: number;
  resolutionScale?: number;
  /**
   * Run SSR on the WebGL2 backend too (default true). three's own SSRNode emits one invalid GLSL
   * line there; the pipeline builds the vendored `render/vendor/SSRNodeCompat.js`, which compiles on
   * both. `false` skips SSR on WebGL2 — and then its three MRT targets are not requested either.
   */
  webgl2?: boolean;
}

export interface DOFConfig {
  /** Focus distance in world units (default 10). */
  focusDistance?: number;
  /**
   * NOT a lens focal length: this is the FOCUS RANGE in world units — how far
   * an object may be from the focus plane before it is COMPLETELY out of
   * focus. three computes `CoC = smoothstep(0, focalLength, |viewZ| −
   * focusDistance)`, so a small value blurs everything and a large value blurs
   * only what is far from the plane. Read as metres, not millimetres: a "50 mm
   * lens" reading of 0.05 means "fully blurred 5 cm off the focus plane"
   * (2026-09-14). Default 40 m.
   */
  focalLength?: number;
  /** Unitless bokeh size (default 5). */
  bokehScale?: number;
  /**
   * 'hex' switches to the engine's custom hexagonal-aperture gather bokeh
   * (BokehDofNode: blue-noise stratified adaptive taps) instead of three's
   * stock dof() — the previously-unwired module (2026-08-17 audit).
   */
  bokeh?: 'standard' | 'hex';
  /**
   * A LENS instead of a focus range (2026-09-25, visual realism R4): when set, `focalLength` and
   * `bokehScale` are derived from the thin-lens circle of confusion at `focusDistance` over the drawing
   * buffer width (render/dof/PhysicalDof.ts). `setDof()` keeps focus / range / bokeh LIVE either way.
   */
  aperture?: { fNumber: number; focalMm?: number; sensorMm?: number };
}

export interface MotionBlurConfig {
  /** Number of reconstruction taps (default 16; 8–32). */
  samples?: number;
  /** Trail-length multiplier on the velocity buffer (default 1). */
  intensity?: number;
  /** Hard cap on the blur radius in pixels (default 48) — a camera flick never streaks the whole screen. */
  maxBlurPx?: number;
  /** Tile size (px) for the dominant-velocity dilation (default 20). */
  tileSize?: number;
  /** Depth-aware tap weighting (default true): foreground blur smears over the background, never the reverse. */
  depthAware?: boolean;
  /** R4 (2026-09-25): a fixed shutter as a fraction of the frame (0.5 = 180°); unset = the 1/90 s time shutter. */
  shutterFraction?: number;
  /** R4: weight on camera-induced blur (default 1; 0.5 keeps a pan readable) and on object blur (default 1). */
  cameraWeight?: number;
  objectWeight?: number;
}

export interface TonemapConfig {
  exposure?: number;
  toneMapping?: THREE.ToneMapping;
  /**
   * Friendly operator name — takes precedence over `toneMapping` when set.
   * 'aces' = ACESFilmic, 'agx' = AgX, 'neutral' = Khronos Neutral,
   * 'reinhard' / 'cineon' / 'linear' / 'none' as named.
   */
  operator?: 'aces' | 'agx' | 'neutral' | 'reinhard' | 'cineon' | 'linear' | 'none';
  /**
   * A custom operator applied AT the display boundary instead of three's
   * (lighting agent 2026-09-25): `(rgbNode) => rgbNode`, fed the exposed
   * linear colour (× renderer.toneMappingExposure); three then only converts
   * the colour space. The engine's hue-preserving ACES 2 node
   * (`render/color/aces2.createAces2ToneMapNode`) is the intended one.
   */
  customNode?: (rgb: unknown) => unknown;
}

/** Map a friendly tonemap operator name to the three.js constant. */
export function toneMappingForOperator(op: NonNullable<TonemapConfig['operator']>): THREE.ToneMapping {
  switch (op) {
    case 'aces': return THREE.ACESFilmicToneMapping;
    case 'agx': return THREE.AgXToneMapping;
    case 'neutral': return THREE.NeutralToneMapping;
    case 'reinhard': return THREE.ReinhardToneMapping;
    case 'cineon': return THREE.CineonToneMapping;
    case 'linear': return THREE.LinearToneMapping;
    case 'none': return THREE.NoToneMapping;
  }
}

// ── New Effect Configs ──────────────────────────────────────────────────────

export interface AfterImageConfig {
  /** Damping factor 0–1 (default 0.96). Higher = longer trails. */
  damp?: number;
}

export interface AnamorphicConfig {
  /** Brightness threshold (default 0.9). */
  threshold?: number;
  /** Streak scale (default 3). */
  scale?: number;
  /** Blur samples (default 32). */
  samples?: number;
}

export interface BoxBlurConfig {
  /** Enable box blur. */
  enabled?: boolean;
}

export interface ChromaticAberrationConfig {
  /** Aberration strength (default 1.0). */
  strength?: number;
  /** Scale factor for stepped scaling from center (default 1.1). */
  scale?: number;
}

export interface DenoiseConfig {
  /** Enable denoising. Uses depth + normals from scene pass. */
  enabled?: boolean;
}

export interface DotScreenConfig {
  /** Pattern angle in radians (default pi/2 ≈ 1.57). */
  angle?: number;
  /** Pattern scale (default 1). */
  scale?: number;
}

export interface FilmConfig {
  /** Film grain intensity (default 1; 0.01–0.02 for 'luma'). */
  intensity?: number;
  /**
   * 'three' (default): three's FilmNode, in the HDR chain. 'luma' (lighting
   * agent 2026-09-25, §10 R11): a zero-mean, luminance-weighted grain plus a
   * ±½-LSB dither, DISPLAY-referred and after AA / upscale (AMD's order) — it
   * doubles as the banding dither.
   */
  mode?: 'three' | 'luma';
}

export interface GaussianBlurConfig {
  /** Blur kernel size (default 4). */
  sigma?: number;
  /** Resolution scale 0–1 (default 1). */
  resolutionScale?: number;
}

export interface GrayscaleConfig {
  /** Enable grayscale desaturation. */
  enabled?: boolean;
}

export interface HashBlurConfig {
  /** Blur amount (default 0.1). */
  amount?: number;
}

export interface Lut3DConfig {
  /** 3D LUT texture (required). */
  lut: THREE.Data3DTexture;
  /** LUT grid size (e.g. 8, 16, 32). */
  size: number;
  /** LUT intensity 0–1 (default 1). */
  intensity?: number;
}

export interface OutlineConfig {
  /** Objects to outline (required). */
  selectedObjects: THREE.Object3D[];
}

export interface RGBShiftConfig {
  /** Shift amount (default 0.005). */
  amount?: number;
  /** Shift angle in radians (default 0). */
  angle?: number;
}

export interface SepiaConfig {
  /** Enable sepia color grading. */
  enabled?: boolean;
}

export interface SMAAConfig {
  /** Enable SMAA anti-aliasing (mutually exclusive with FXAA/TRAA). */
  enabled?: boolean;
  /** AA quality 2|4|8|16|32 → log2(q) iterated passes (see the AA section). */
  quality?: number;
}

export interface SobelConfig {
  /** Enable Sobel edge detection. */
  enabled?: boolean;
}

export interface SSGIConfig {
  /** Enable screen-space global illumination. */
  enabled?: boolean;
  /** Slices per pixel (1–4; three's temporal presets: low 1, med 2, high 3). */
  sliceCount?: number;
  /** Steps per slice (1–32; temporal presets: 12 / 8 / 16). */
  stepCount?: number;
  /** Temporal filtering (default true; false needs a denoise pass). */
  temporal?: boolean;
  /** Indirect-light strength multiplier (default 1). */
  intensity?: number;
  /** How much of SSGI's own ambient-occlusion term darkens the scene (0..1, default 0.5). */
  aoStrength?: number;
}

export interface PostTransitionConfig {
  /** Second scene to transition to (required). */
  sceneB: THREE.Scene;
  /** Camera for second scene (required). */
  cameraB: THREE.Camera;
  /** Transition pattern texture. */
  mixTexture?: THREE.Texture;
  /** Blend ratio 0–1. 0 = scene A, 1 = scene B (default 0). */
  mixRatio?: number;
  /** Interpolation threshold (default 0.1). */
  threshold?: number;
  /** Whether mix texture influences transition (default false). */
  useTexture?: boolean;
}

export interface TRAAConfig {
  /** Enable Temporal Resolve Anti-Aliasing (mutually exclusive with FXAA/SMAA). */
  enabled?: boolean;
  /** AA quality 2|4|8|16|32 — ≥8 chains one FXAA behind the temporal resolve
   *  (TRAA itself must run exactly once; see the AA section). */
  quality?: number;
}

export interface ContactShadowsPassConfig extends Partial<ContactShadowsConfig> {
  /**
   * Set to false to disable the pass entirely (default: true when key is present).
   * The pass is only included in the pipeline when this key exists in PostProcessingConfig.
   */
  enabled?: boolean;
}

export interface CloudsPassConfig {
  /**
   * The cloud RGBA node — `.rgb` = in-scattered radiance, `.a` = opacity
   * (`1 - transmittance`). Produced by `CloudRenderer.buildCompositeNode()`.
   *
   * Compositing as a DEPTH-AWARE post pass (rather than in
   * `scene.backgroundNode`) is what makes the clouds world-anchored: the
   * cloud node ray-marches an altitude slab from the REAL camera position
   * (`ro = cameraPosition`, `rd` reconstructed from `screenUV` + the camera
   * matrices), so the clouds parallax + pan with the world, and the sky-pixel
   * depth mask lets terrain/buildings occlude them. In the background skybox
   * (a unit sphere drawn camera-locked at the far plane via a w=0 model-view
   * transform) the same finite-altitude clouds read as pinned to the view.
   */
  node: { rgb: unknown; a: unknown };
  /** Raymarch at this fraction of the drawing buffer (0.5 = quarter of the rays); default 1 = full. */
  resolutionScale?: number;
  /** Set false to skip the pass even when a node is supplied. Default true. */
  enabled?: boolean;
  /**
   * Composite AFTER the anti-aliasing stage instead of slot 0. Use with
   * TRAA: the temporal resolve's per-frame camera jitter makes the clouds'
   * binary depth mask flicker at geometry silhouettes and its blue-noise
   * ray jitter defeats the history clamp — the resolve smears both into
   * blocky dither. Post-AA clouds skip bloom (acceptable: they are lit
   * scattering, not emitters) but stay crisp and unsmeared.
   * Honoured only when a temporal resolve is built (`cloudsCompositeAfterAA`, gaps wave round 2) — elsewhere the
   * clouds go in at slot 0, in the linear chain.
   */
  afterAA?: boolean;
}

// ── Pipeline Config ─────────────────────────────────────────────────────────

/**
 * Spatial upscaling (FSR 1: EASU + RCAS) — the scene pass renders at
 * output / scaleFactor and the 12-tap edge-adaptive kernel reconstructs the
 * output resolution right after it, so every later pass sees a full-size
 * image. Cheaper than raw render scale at the same fragment count and much
 * sharper; the render scale can also be moved LIVE (`setRenderScale`) for a
 * frame-time governor without a rebuild.
 */
const _upscaleSize = new THREE.Vector2();

/**
 * THE LOW-RES SIZE LAW (2026-09-26, materials agent): the render-resolution size of the scene pass at `scale`, with
 * three's OWN arithmetic (PassNode.setSize: `Math.floor(width * resolutionScale)`, resolutionScale = 1 / scale). Every
 * RTT, EASU source size and log line of the upscale stage uses this — `Math.round(w / scale)` said 985×554 where the
 * pass is 984×553 at 1.3× on 1280×720, and TAAU loads the pass's depth / velocity on the beauty's texel grid.
 */
/**
 * Whether the SSR pass will be BUILT for this config on this backend (gaps wave round 2, 2026-09-28). One rule for
 * the pass and for its three MRT targets (normal, metalness, roughness) — the targets used to be requested whenever
 * `ssr` was set, including on WebGL2 where the pass skipped itself. WebGPU always; WebGL2 unless `ssr.webgl2` is
 * false (the vendored node compiles there).
 */
/**
 * THE BUILT-PASS MANIFEST (shader gaps program, 2026-09-29 — the report's §14.2): what the pipeline ACTUALLY built on
 * this device, not what the preset asked for. `requested` is read from the config, `built` is the graph order (each
 * entry's `domain` says whether it runs on scene-linear HDR or after the display transform), `skipped` carries the
 * reason where the pipeline decides one, and `attachments` / `attachmentBytes` / `attachmentCap` / `dropped` are the
 * scene pass's MRT against the device's colour-attachment budget. "SSR is on but I cannot see it" is answered here.
 */
export interface PassManifest {
  backend: 'webgpu' | 'webgl2';
  /** The scene pass's MRT layout ('output' first); ['output'] when no MRT was installed. */
  attachments: string[];
  /** 8 bytes a sample per RGBA16F target. */
  attachmentBytes: number;
  /** The device's maxColorAttachmentBytesPerSample (null when no WebGPU device reports one). */
  attachmentCap: number | null;
  /** Consumers THE ATTACHMENT BUDGET dropped. */
  dropped: string[];
  /** Stages the config asked for. */
  requested: string[];
  /** Built stages in graph order; `display` = after the tone map / output transform. */
  built: Array<{ name: string; domain: 'hdr' | 'display' }>;
  /** Requested stages that were not built, with the pipeline's reason. */
  skipped: Array<{ name: string; reason: string }>;
  /** The temporal resolve that runs. */
  temporal: 'taau' | 'traa' | 'none';
  /** Incremented by every `resetHistory` (a camera cut, a teleport, a world swap). */
  historyEpoch: number;
  /** Why the last reset happened. */
  lastReset: string | null;
  /** The scene pass's render target (render resolution), when set up. */
  sceneSize: [number, number] | null;
  /** The drawing buffer. */
  outputSize: [number, number];
  /** The upscale this build runs. */
  upscale: { mode: 'fsr' | 'taau'; ratio: number } | null;
  /** THE ONE EVALUATION: the shared chain RTTs' scale of the drawing buffer (1 / ratio under AA-first FSR or TAAU); 'legacy' = per-consumer conversion. */
  chain: { textures: 'shared' | 'legacy'; scale: number; count: number };
}

/** config key → the pass name the pipeline registers (the stages a manifest reports as requested). */
const MANIFEST_STAGES: ReadonlyArray<[string, string]> = [
  ['clouds', 'clouds'], ['aerial', 'aerial'], ['volumetricFog', 'volumetricFog'], ['ao', 'ao'], ['ssgi', 'ssgi'],
  ['ssr', 'ssr'], ['denoise', 'denoise'], ['contactShadows', 'contactShadows'], ['capsuleShadows', 'capsuleShadows'],
  ['godRays', 'godRays'], ['dof', 'dof'], ['lensFlares', 'lensFlares'], ['motionBlur', 'motionBlur'], ['bloom', 'bloom'],
  ['anamorphic', 'anamorphic'], ['afterImage', 'afterImage'], ['outline', 'outline'], ['film', 'film'], ['traa', 'traa'],
  ['smaa', 'smaa'], ['fxaa', 'fxaa'], ['upscale', 'upscale'],
];

/** The stages a config asks for (an `enabled: false` sub-config asks for nothing). Pure — the manifest's `requested`. */
export function requestedStages(config: PostProcessingConfig | undefined | null): string[] {
  if (!config) return [];
  const out: string[] = [];
  for (const [key, name] of MANIFEST_STAGES) {
    const v = (config as Record<string, unknown>)[key];
    if (!v) continue;
    if (typeof v === 'object' && (v as { enabled?: boolean }).enabled === false) continue;
    out.push(name);
  }
  // TAAU is requested through the upscale; it is built as the 'traa' pass.
  if (config.upscale?.mode === 'taau' && !out.includes('traa')) out.push('traa');
  return out;
}

export function ssrWillRun(ssr: SSRConfig | undefined | null, webgpu: boolean): boolean {
  if (!ssr) return false;
  return webgpu || ssr.webgl2 !== false;
}

/**
 * Whether the clouds are composited AFTER the anti-aliasing stage (gaps wave round 2). `afterAA` exists to keep a
 * TEMPORAL resolve (TAAU / TRAA) from smearing the clouds' dither — with no temporal pass it only moved the linear
 * cloud radiance past the display transform (FXAA and the AA-first FSR path tone-map first), so it is honoured only
 * when a temporal resolve is actually built: a velocity target exists and TAAU or TRAA is on.
 */
export function cloudsCompositeAfterAA(afterAA: boolean | undefined, temporalResolve: boolean): boolean {
  return !!afterAA && temporalResolve;
}

export function lowResSize(w: number, h: number, scale: number): [number, number] {
  const rs = 1 / scale;
  return [Math.max(1, Math.floor(w * rs)), Math.max(1, Math.floor(h * rs))];
}
export interface UpscaleConfig {
  /** FSR quality preset (ultra 1.3×, quality 1.5×, balanced 1.7×, performance 2×) — sets the render ratio for both modes. */
  quality: FSRQuality;
  /** RCAS sharpness 0..1 (default: the preset's). */
  sharpness?: number;
  /**
   * 'fsr' (default): spatial — EASU right after the scene pass.
   * 'taau': TEMPORAL — the scene pass renders at 1/ratio and the temporal
   * resolve (three's TRAA node, auto-enabled, jittered, history-clamped)
   * accumulates the sub-pixel samples into an output-resolution history at
   * the AA slot; RCAS sharpens the resolve. Needs the velocity MRT (forced).
   */
  mode?: 'fsr' | 'taau';
  /** EASU kernel: 'amd' (default — FidelityFX FSR 1's direction-adaptive Lanczos-2) or 'legacy' (the first, bilinear-like kernel). */
  kernel?: 'amd' | 'legacy';
}

/**
 * The AA / upscale ORDER (2026-09-25, visual realism R3). Separate from
 * `upscale` so an app that builds `upscale` from its own quality table can
 * set it from its preset without touching that code path.
 */
export interface UpscaleOrderConfig {
  /**
   * FSR tiers: tone-map and anti-alias at RENDER resolution, then EASU + RCAS
   * at output — AMD's documented order ("FSR 1 expects an anti-aliased,
   * perceptual-space input"; EASU on raw linear HDR both upscales every
   * staircase and rings around the sun). The whole HDR chain is then
   * evaluated at render resolution too (a cheaper final composite). The
   * display transform moves to this point, so everything after the AA slot
   * stays display-referred exactly as before. Default false.
   */
  aaFirst?: boolean;
  /** EASU kernel override (else `upscale.kernel`, else 'amd'). */
  kernel?: 'amd' | 'legacy';
}

export interface PostProcessingConfig {
  /** FSR 1 upscaling of the scene pass (see UpscaleConfig). */
  upscale?: UpscaleConfig;
  /** AA before upscale + the EASU kernel (see UpscaleOrderConfig). */
  upscaleOrder?: UpscaleOrderConfig;
  // Existing
  bloom?: BloomConfig;
  ao?: AOConfig;
  fxaa?: FXAAConfig;
  ssr?: SSRConfig;
  dof?: DOFConfig;
  motionBlur?: MotionBlurConfig;
  tonemap?: TonemapConfig;
  godRays?: Partial<GodRaysConfig> & { enabled?: boolean };
  /**
   * Screen-space (pseudo) lens flares — ghosts, starburst and streak from the
   * frame's own bright pixels (John Chapman 2013); no light positions needed.
   * Composited right after the god rays, before bloom/tonemap so it reads HDR.
   */
  lensFlares?: Partial<LensFlareConfig> & { enabled?: boolean };
  // New
  afterImage?: AfterImageConfig;
  anamorphic?: AnamorphicConfig;
  boxBlur?: BoxBlurConfig;
  chromaticAberration?: ChromaticAberrationConfig;
  denoise?: DenoiseConfig;
  dotScreen?: DotScreenConfig;
  film?: FilmConfig;
  gaussianBlur?: GaussianBlurConfig;
  grayscale?: GrayscaleConfig;
  hashBlur?: HashBlurConfig;
  lut3D?: Lut3DConfig;
  outline?: OutlineConfig;
  rgbShift?: RGBShiftConfig;
  sepia?: SepiaConfig;
  smaa?: SMAAConfig;
  sobel?: SobelConfig;
  ssgi?: SSGIConfig;
  transition?: PostTransitionConfig;
  traa?: TRAAConfig;
  /** Screen-space contact shadows — cheap high-frequency shadows near surfaces. */
  contactShadows?: ContactShadowsPassConfig;
  /** Analytic capsule shadows for character proxies (soft, map-free). */
  capsuleShadows?: CapsuleShadowsPassConfig;
  /**
   * Volumetric clouds composited as a depth-aware post pass. Composited EARLY
   * (right after the scene pass, before god rays / bloom / tone-map) so the
   * clouds receive bloom + tone-mapping like the rest of the HDR scene, and
   * only over sky pixels (depth ≈ far) so opaque geometry occludes them.
   */
  clouds?: CloudsPassConfig;
  /**
   * Accessibility colourblind correction. An optional final, full-screen
   * colour transform applied AFTER tone-mapping + anti-aliasing (i.e. on the
   * display-referred image). Supplied by the accessibility subsystem's
   * `AccessibilityModule.createColorblindTransform()`. When omitted the
   * pipeline is unchanged — fully backward-compatible.
   */
  colorblind?: ColorblindPassConfig;
  /**
   * Hillaire aerial-perspective composite — attenuates + haze-tints scene
   * geometry from the baked froxel volume (SkyLutSet.aerial). Applied right
   * after the clouds slot, on the raw HDR scene colour, so the haze flows
   * through bloom/tone-map like the sky it matches.
   */
  aerial?: AerialCompositeConfig;
  /** Lift/gamma/gain parametric colour grading (pre-LUT). */
  colorGrade?: ColorGradeConfig;
  /**
   * Analytic volumetric fog (closed-form height fog + sun in-scatter + local
   * box volumes) — applied right after aerial perspective, pre-AO/bloom.
   */
  volumetricFog?: VolumetricFogPassConfig;
  /** Radial vignette (post-AA, display-referred). */
  vignette?: VignetteConfig;
  /** Barrel/pincushion lens distortion (post-AA — it resamples the chain). */
  lensDistortion?: LensDistortionConfig;
  /** RCAS-style neighborhood-clamped sharpen (post-AA, pre-distortion). */
  sharpen?: SharpenConfig;
  /**
   * The Purkinje / mesopic shift (2026-09-27, `color/purkinje.ts`): a rod-vision desaturation toward blue-grey, in the
   * linear domain just before the display transform. Strength 0 (the default uniform) is an identity; the app drives
   * `purkinjeUniforms` from its adaptation luminance each frame.
   */
  purkinje?: { enabled?: boolean };
  /**
   * THE ONE EVALUATION (shader sweep 2026-10-06). 'shared' (default): a stage that needs the HDR chain as a TEXTURE
   * (SSR, SSGI, DOF, lens flares, motion blur, bloom, SMAA) reads ONE render target of it — rendered ONCE a frame, at
   * the chain's own resolution (the scene pass's under AA-first FSR / TAAU) — and every stage after it reads that
   * texture instead of re-evaluating the chain above it. 'legacy': each stage converts its input itself (three's
   * default RTTs re-render for EVERY render call that samples them, at the drawing buffer's size) and the chain is
   * re-evaluated downstream — the pre-sweep graph, kept as the A/B and the way back.
   */
  chainTextures?: 'shared' | 'legacy';
}

export interface ColorblindPassConfig {
  /**
   * Colour transform `(rgbNode) => rgbNode`. Applied as the very last step.
   * Set to false / omit to disable. The accessibility module rebuilds the
   * pipeline (or swaps this transform) when the colourblind mode changes.
   */
  transform?: (rgb: unknown) => unknown;
  /** Set false to skip even when a transform is present. Default true. */
  enabled?: boolean;
}

// ── Implementation ──────────────────────────────────────────────────────────

interface PassEntry {
  name: string;
  node: unknown;
  enabled: boolean;
}

export class PostProcessingPipeline {
  private renderer: THREE.WebGPURenderer;
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  // DOF near/far uniforms — refreshed in setCamera() and on per-frame render
  // when the camera's near/far have drifted from the cached values. Typed
  // as `any` because TSL `uniform()` returns a Node subclass that exposes
  // `.value`; we don't import the runtime type.
   
  private _dofNearUniform: any = null;
   
  private _dofFarUniform: any = null;
  /** Live DOF inputs (R4): focus distance, focus range, bokeh radius scale — `setDof()`. */
  private _dofFocus: any = null;
  private _dofRange: any = null;
  private _dofBokeh: any = null;
  private postProcessing: THREE.PostProcessing | null = null;
  private passes = new Map<string, PassEntry>();
  private _enabled = true;
  private _ready = false;
  private _disposed = false;
  private _godRays: GodRaysNodeResult | null = null;
  private _lensFlare: LensFlareNodeResult | null = null;
  /** Dual-Kawase bloom (stage 4) — resized with the drawing buffer. */
  private _kawase: { setSize(w: number, h: number): void; dispose?(): void } | null = null;
  private _contactShadowsUniforms: ContactShadowsUniforms | null = null;
  private _aerialUniforms: AerialCompositeUniforms | null = null;
  private _vignetteUniforms: VignetteUniforms | null = null;
  private _lensDistortionUniforms: LensDistortionUniforms | null = null;
  private _colorGradeUniforms: ColorGradeUniforms | null = null;
  /** The Purkinje shift's uniforms (null when the config did not ask for it). */
  private _purkinje: PurkinjeUniforms | null = null;
  private _sharpenUniforms: SharpenUniforms | null = null;
  private _volumetricFogUniforms: VolumetricFogUniforms | null = null;
  private _capsuleShadowsUniforms: CapsuleShadowsUniforms | null = null;
  private _scenePass: any = null; // PassNode — stored to update camera reference
  /** Fractional-resolution cloud RTT (null = full-res clouds or no clouds). */
  private _cloudRtt: { node: { setSize?: (w: number, h: number) => void }; rs: number; w: number; h: number } | null = null;
  /** Live FSR state (null = no upscale in this build). */
  private _upscale: { scale: number; mode: 'fsr' | 'taau'; easu: EASUNodeResult | null; rcas: RCASNodeResult | null; w: number; h: number } | null = null;
  /** Render-resolution RTTs of the AA-first / TAAU paths (sized with the live render scale; disposed with the pipeline). */
  private _lowResRtts: Array<{ setSize?: (w: number, h: number) => void; renderTarget?: { dispose?: () => void } }> = [];
  /** THE ONE EVALUATION: the shared chain RTTs (auto-resized at `_chainScale` × the drawing buffer; disposed with the pipeline). */
  private _chainRtts: Array<{ setResolutionScale?: (s: number) => void; renderTarget?: { dispose?: () => void } }> = [];
  /** Output-resolution RTTs this pipeline made (THE ONE EVALUATION's EASU copy) — disposed with it, never re-scaled. */
  private _outRtts: Array<{ renderTarget?: { dispose?: () => void } }> = [];
  /** The chain's resolution as a fraction of the drawing buffer (1 / the upscale under AA-first FSR / TAAU, else 1). */
  private _chainScale = 1;
  /** True when the chain is consumed at the RENDER resolution (AA-first FSR / TAAU): its RTTs follow setRenderScale. */
  private _chainAtRender = false;
  /** `chainTextures: 'legacy'` for this build (the manifest reports it). */
  private _chainLegacy = false;
  /** The DOF's bokeh is in pixels of its INPUT: × this so a chain-resolution input blurs the same OUTPUT pixels. */
  private _dofBokehK = 1;
  /** True when this build resolves temporally (TRAA or TAAU) — the mip-bias / camera consumers read it. */
  private _temporal = false;
  /** The GTAO node + the tier's AO scale as a fraction of the RENDER resolution (THE AO-GRID LAW; re-aimed by setRenderScale). */
  private _ao: { node: { resolutionScale?: number }; base: number } | null = null;
  /** The SSR node + the tier's SSR scale as a fraction of the RENDER resolution (THE AO-GRID LAW applied to SSR, 2026-09-27). */
  private _ssr: { node: { resolutionScale?: number }; base: number } | null = null;
  /** The setup-time half of the built-pass manifest (THE BUILT-PASS MANIFEST; the rest is read live by passManifest()). */
  private _manifest: { attachments: string[]; attachmentCap: number | null; dropped: string[]; skips: Map<string, string>; requested: string[]; lastReset: string | null } =
    { attachments: ['output'], attachmentCap: null, dropped: [], skips: new Map(), requested: [], lastReset: null };
  /** THE HISTORY EPOCH (2026-09-29): bumped by resetHistory; frames the motion blur stays off after a reset. */
  private _historyEpoch = 0;
  private _historyHold = 0;
  private _mbIntensityHeld: number | null = null;
  /** Extra PassNodes this pipeline created (e.g. the transition's scene B pass) — disposed with it. */
  private _ownedPassNodes: unknown[] = [];
  private _setupPromise: Promise<void>;

  /** Access god rays result for per-frame sun position uniform updates. */
  get godRays(): GodRaysNodeResult | null { return this._godRays; }
  /** The lens-flare node result (uniforms: brightThreshold, ghost/starburst/streak intensities) or null. */
  get lensFlare(): LensFlareNodeResult | null { return this._lensFlare; }

  /**
   * Access contact shadows uniforms for per-frame light direction updates.
   * `render()` refreshes `lightDirView` and `lightFade` each frame from
   * `getPrimaryLightState(scene, camera)` (the shadows barrel).
   */
  get contactShadowsUniforms(): ContactShadowsUniforms | null { return this._contactShadowsUniforms; }

  /** Projection coefficients, near/far and the primary light direction feeding
   *  the contact-shadow march — nothing refreshed them before (no system was
   *  ever instantiated), so FOV/resize changes and the sun direction were ignored. */
  private _refreshContactShadowUniforms(): void {
    const u = this._contactShadowsUniforms;
    if (!u) return;
    const cam = this.camera as THREE.Camera & { near?: number; far?: number };
    const pm = cam.projectionMatrix.elements;
    u.m11.value = pm[0]; u.m22.value = pm[5]; u.m33.value = pm[10]; u.m43.value = pm[14];
    u.invM11.value = 1 / (pm[0] === 0 ? 1e-6 : pm[0]);
    u.invM22.value = 1 / (pm[5] === 0 ? 1e-6 : pm[5]);
    if (typeof cam.near === 'number') u.near.value = cam.near;
    if (typeof cam.far === 'number') u.far.value = cam.far;
    // The key light = the BRIGHTEST shown DirectionalLight, and its fade (0 below the horizon / with the light
    // off) — the first one found was the sun even under the moon (2026-09-27).
    try {
      const key = getPrimaryLightState(this.scene, this.camera);
      u.lightDirView.value.copy(key.dirView);
      u.lightFade.value = key.fade;
    } catch { /* no light yet */ }
  }

  /** Resolves when async setup is complete. Useful for tests. */
  get ready(): Promise<void> { return this._setupPromise; }
  /** True once the async setup built a chain (false while building, after a failed setup, or after dispose). */
  get isReady(): boolean { return this._ready; }
  /** The Purkinje shift's uniforms (strength / keyLevel / tint) — null when not configured. */
  get purkinjeUniforms(): PurkinjeUniforms | null { return this._purkinje; }

  /** Aerial-perspective uniforms (camera params refresh via setCamera). */
  get aerialUniforms(): AerialCompositeUniforms | null { return this._aerialUniforms; }
  /** Vignette uniforms for runtime control. */
  get vignetteUniforms(): VignetteUniforms | null { return this._vignetteUniforms; }
  /** Lens-distortion uniforms for runtime control. */
  get lensDistortionUniforms(): LensDistortionUniforms | null { return this._lensDistortionUniforms; }
  /** Parametric colour-grade uniforms for runtime control. */
  get colorGradeUniforms(): ColorGradeUniforms | null { return this._colorGradeUniforms; }
  /** Sharpen uniforms for runtime control. */
  get sharpenUniforms(): SharpenUniforms | null { return this._sharpenUniforms; }
  /** Volumetric fog uniforms (sun dir, density, volumes) for runtime control. */
  get volumetricFogUniforms(): VolumetricFogUniforms | null { return this._volumetricFogUniforms; }
  /** Capsule-shadow uniforms (per-capsule slots, light dir) for runtime control. */
  get capsuleShadowsUniforms(): CapsuleShadowsUniforms | null { return this._capsuleShadowsUniforms; }

  /**
   * The HDR scene pass render target (null until setup completes). Consumers:
   * AutoExposureSystem reads luminance from it.
   */
  get scenePassRenderTarget(): { width: number; height: number } | null {
    return (this._scenePass as { renderTarget?: { width: number; height: number } } | null)
      ?.renderTarget ?? null;
  }

  constructor(
    renderer: THREE.WebGPURenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    config?: PostProcessingConfig,
  ) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;

    this._setupPromise = this._setup(config);
  }

  private async _setup(config?: PostProcessingConfig): Promise<void> {
    this._manifest.requested = requestedStages(config);
    const skip = (name: string, reason: string): void => { this._manifest.skips.set(name, reason); };
    try {
      // Tone-map config: three's PostProcessing applies renderer.toneMapping +
      // toneMappingExposure in its display-output transform, so the pipeline
      // consumes this config by writing renderer state. (Was declared but
      // never read — 2026-08-17 shader audit bug #2.)
      if (config?.tonemap) {
        const tm = config.tonemap.operator !== undefined
          ? toneMappingForOperator(config.tonemap.operator)
          : config.tonemap.toneMapping;
        if (tm !== undefined) this.renderer.toneMapping = tm;
        if (config.tonemap.exposure !== undefined) this.renderer.toneMappingExposure = config.tonemap.exposure;
        Logger.info('PostProcessing', `Tonemap applied (operator=${config.tonemap.operator ?? 'explicit'}, exposure=${this.renderer.toneMappingExposure})`);
      }

      // three r185 renamed PostProcessing → RenderPipeline (the old name logs a deprecation every construction).
      let PipelineCtor = THREE.PostProcessing;
      try { const rp = (THREE as unknown as { RenderPipeline?: typeof THREE.PostProcessing }).RenderPipeline; if (rp) PipelineCtor = rp; } catch { /* a mocked namespace without the r185 export */ }
      this.postProcessing = new PipelineCtor(this.renderer);

      // Create scene pass and extract texture nodes (Three.js r182 API)
      const scenePass = pass(this.scene, this.camera);
      this._scenePass = scenePass; // Store for setCamera updates
      const scenePassColor = (scenePass as any).getTextureNode('output');
      const scenePassDepth = (scenePass as any).getTextureNode('depth');

      // Only request MRT outputs when effects that consume them are enabled.
      // Calling getTextureNode('normal') etc. unconditionally tells PassNode
      // to add extra color attachments, which causes WebGPU validation errors
      // when standard materials don't output to those targets.
      // SSR's three targets follow the pass that will actually be BUILT (gaps wave round 2, 2026-09-28): the old
      // `!!config.ssr` requested normal + metalness + roughness on WebGL2 while the SSR block skipped itself there —
      // three RGBA16F targets nothing read. `ssrWillRun` is the one rule both places read.
      const ssrRuns = ssrWillRun(config?.ssr, this._isWebGPU());
      let needsNormal = !!(config?.ao || config?.ssgi || ssrRuns || config?.denoise);
      const taauWanted = config?.upscale?.mode === 'taau' && this._taauSupported();
      let needsVelocity = !!(config?.motionBlur || config?.traa?.enabled || taauWanted);
      // THE PACKED MATERIAL TARGET (shader gaps program, 2026-09-29): SSR's metalness and roughness share ONE target
      // (`metalrough` = vec4(metalness, roughness, 0, 1) — three's own r185+ SSR example packs them the same way), so
      // output + normal + velocity + metalrough is 32 bytes a sample and a spec-default 32-byte device keeps SSR (two
      // separate RGBA16F targets made it 40, and the budget below dropped SSR on every such device).
      let needsMetalRough = ssrRuns;
      // THE ATTACHMENT BUDGET (coordinator review, 2026-09-27): every scene-pass target is RGBA16F (8 bytes a sample)
      // and WebGPU caps their SUM at the device's maxColorAttachmentBytesPerSample (the spec default is 32 — four
      // targets). Output + normal + velocity + SSR's metalness + roughness is 40, and a pipeline over the cap is
      // REJECTED ("Total color attachment bytes per sample (40) exceeds maximum (32)" — the contact bed's MRT leg):
      // every scene material fails and the frame breaks. Drop the least essential consumers until it fits — SSR's
      // pair, then velocity, then normal; each consumer already skips itself with a warning when its target is absent.
      {
        const be = (this.renderer as unknown as { backend?: { isWebGPUBackend?: boolean; device?: { limits?: { maxColorAttachmentBytesPerSample?: number } } } }).backend;
        const lim = be?.device?.limits?.maxColorAttachmentBytesPerSample; // a live device always reports it
        const cap = be?.isWebGPUBackend && typeof lim === 'number' ? lim : Infinity;
        const bytes = (): number => 8 * (1 + [needsNormal, needsVelocity, needsMetalRough].filter(Boolean).length);
        const dropped: string[] = [];
        if (bytes() > cap && needsMetalRough) { needsMetalRough = false; dropped.push('SSR (metalrough)'); }
        if (bytes() > cap && needsVelocity) { needsVelocity = false; dropped.push('velocity (TAAU / TRAA / motion blur)'); }
        if (bytes() > cap && needsNormal) { needsNormal = false; dropped.push('normal (AO / SSGI / denoise)'); }
        if (dropped.length) Logger.warn('PostProcessing', `Scene pass over the device's ${cap}-byte colour-attachment budget — dropped ${dropped.join(', ')}`);
        this._manifest.attachmentCap = Number.isFinite(cap) ? cap : null;
        this._manifest.dropped = dropped;
      }

      let scenePassNormal: any = null;
      let scenePassVelocity: any = null;
      let scenePassMetalRough: any = null;
      if (needsNormal) try { scenePassNormal = (scenePass as any).getTextureNode('normal'); } catch { /* not available */ }
      if (needsVelocity) try { scenePassVelocity = (scenePass as any).getTextureNode('velocity'); } catch { /* not available */ }
      if (needsMetalRough) try { scenePassMetalRough = (scenePass as any).getTextureNode('metalrough'); } catch { /* not available */ }
      // SSR reads the two channels of the one target (`.r` metalness, `.g` roughness).
      const scenePassMetalness: any = scenePassMetalRough ? scenePassMetalRough.r : null;
      const scenePassRoughness: any = scenePassMetalRough ? scenePassMetalRough.g : null;

      // THE MRT CONTRACT (r185): getTextureNode('velocity'|'normal'|…) only
      // ADDS the extra color attachment to the pass render target — it does
      // NOT make materials write it. Without pass.setMRT(mrt({...})),
      // renderer.getMRT() is null during the scene pass, every material
      // builds a single-output fragment shader, and Dawn rejects EVERY
      // pipeline against the multi-attachment pass ("Attachment state of
      // [RenderPipeline "Background.material"] is not compatible" — the
      // TRAA/motion-blur black screen). three's own postprocessing examples
      // always pair the two calls; mirror them with the canonical writer
      // node per output.
      if (scenePassNormal || scenePassVelocity || scenePassMetalRough) {
        try {
          const tsl = await import('three/tsl');
          if (this._disposed) return;
          const layout: Record<string, unknown> = { output: tsl.output };
          // Each surface output branches on the material being built: a marked
          // overlay (post/mrtOverlay.ts) writes alpha-0 zeros, everything else the
          // node it always wrote.
          // r185: `normalView` IS the transformed (normal-mapped) view normal; `transformedNormalView` is its
          // deprecated alias and warns on every build (2026-09-26).
          if (scenePassNormal) layout.normal = mrtSurfaceOutput(tsl.normalView);
          if (scenePassVelocity) layout.velocity = mrtSurfaceOutput(tsl.velocity);
          // alpha 1: a transparent SURFACE (water, glass) blended by the material owns the pixel's metal/rough outright.
          if (scenePassMetalRough) layout.metalrough = mrtSurfaceOutput(tsl.vec4(tsl.metalness, tsl.roughness, 0, 1));
          const mrtNode = tsl.mrt(layout as Parameters<typeof tsl.mrt>[0]);
          // The surface targets blend by the MATERIAL's function (post/mrtOverlay.ts):
          // three's per-target default is NoBlending, which let every transparent
          // overlay REPLACE the ground's normal under it — GTAO's black squares
          // under footprints and light rectangles around rain (2026-09-17).
          const blended = installMrtOverlayBlending(mrtNode, Object.keys(layout));
          (scenePass as { setMRT: (m: unknown) => void }).setMRT(mrtNode);
          Logger.info('PostProcessing', `Scene pass MRT installed (${Object.keys(layout).join(', ')}; material blending on ${blended.join(', ') || 'none'})`);
          this._manifest.attachments = Object.keys(layout);
        } catch (err) {
          Logger.warn('PostProcessing', `Scene pass MRT setup failed: ${err}`);
        }
      }

      // r185 STALE BACKGROUND PIPELINE guard: the scene pass's color-target
      // COUNT changes whenever a rebuild adds/removes an MRT output (velocity
      // for TRAA/motion blur, normal for AO/SSR). Regular scene materials
      // recompile against the new attachment state, but three's Background.js
      // only flags its internal NodeMaterial needsUpdate when the background
      // NODE's cache key changes — its cached single-target pipeline then
      // fails Dawn's strict attachment validation and the whole command
      // buffer dies (black screen: "Attachment state of [RenderPipeline
      // "Background.material"] is not compatible"). Wrapping the background
      // node in a FRESH vec4 changes its cache key, forcing the Background
      // path to rebuild its material against the CURRENT targets. Always wrap
      // the ORIGINAL node (tracked on the scene) so repeated rebuilds never
      // nest wrappers. Visually a no-op; runs once per pipeline (re)build.
      const sceneWithBg = this.scene as {
        backgroundNode?: unknown; userData?: Record<string, unknown>;
      };
      if (sceneWithBg.backgroundNode) {
        const ud = (sceneWithBg.userData ??= {});
        // If the app swapped in a NEW background node since our last wrap,
        // adopt it as the original — never resurrect a stale node.
        if (sceneWithBg.backgroundNode !== ud.__bgWrapperNode) {
          ud.__originalBackgroundNode = sceneWithBg.backgroundNode;
        }
        const wrapper = vec4(ud.__originalBackgroundNode as never);
        ud.__bgWrapperNode = wrapper;
        sceneWithBg.backgroundNode = wrapper;
      }

      let outputNode: unknown = scenePassColor;

      // ════════════════════════════════════════════════════════════════════
      // FSR 1 upscale (perf 2026-09-08): the scene pass renders at 1/scale of
      // the drawing buffer; EASU reconstructs the full-size image from the
      // pass texture and RCAS sharpens it. Everything below sees full size.
      // The pass's depth stays at render resolution — depth consumers sample
      // it by screenUV, which is what they did at native anyway.
      // ════════════════════════════════════════════════════════════════════
      this._upscale = null;
      this._temporal = false;
      this._ao = null;
      this._ssr = null;
      this._dofFocus = null; this._dofRange = null; this._dofBokeh = null; // re-created by the DOF block when it builds
      this._disposeCloudRtt();
      this._disposeLowResRtts();
      this._disposeChainRtts();
      // AA before upscale (R3): the EASU is deferred to the AA slot when the tier anti-aliases spatially.
      let fsrDeferred = false;
      const easuKernel = config?.upscaleOrder?.kernel ?? config?.upscale?.kernel ?? 'amd';
      if (config?.upscale && FSR_PRESETS[config.upscale.quality]) {
        const preset = FSR_PRESETS[config.upscale.quality];
        const scale = preset.scaleFactor;
        const size = this.renderer.getDrawingBufferSize(new THREE.Vector2());
        const w = Math.max(1, Math.round(size.x)), h = Math.max(1, Math.round(size.y));
        (scenePass as { setResolutionScale?: (s: number) => void }).setResolutionScale?.(1 / scale);
        if (config.upscale.mode === 'taau' && !taauWanted) { Logger.warn('PostProcessing', 'TAAU needs the WebGPU backend (the velocity MRT drops non-MRT draws on WebGL2) — spatial FSR instead'); skip('traa', 'TAAU needs the WebGPU backend — spatial FSR runs instead'); }
        const spatialAA = !!config.smaa?.enabled || config.fxaa?.enabled !== false;
        if (taauWanted) {
          // Temporal: the resolve happens at the AA slot (TRAA over the low-res
          // pass, output-resolution history); RCAS is appended there.
          this._upscale = { scale, mode: 'taau', easu: null, rcas: null, w, h };
          Logger.info('PostProcessing', `TAAU upscale ${config.upscale.quality} (${scale}×: ${lowResSize(w, h, scale).join('×')} → ${w}×${h}, temporal resolve at the AA slot)`);
        } else if (config.upscaleOrder?.aaFirst && spatialAA && !config.traa?.enabled) {
          // AMD's order: tone map → AA at render resolution → EASU → RCAS, built at the AA slot below.
          fsrDeferred = true;
          this._upscale = { scale, mode: 'fsr', easu: null, rcas: null, w, h };
          Logger.info('PostProcessing', `FSR 1 upscale ${config.upscale.quality} (${scale}×) — AA at render resolution BEFORE EASU (${easuKernel} kernel)`);
        } else {
          // WHY the pass texture NODE, never its `.value`: a PassNode renders
          // only when a PassTextureNode referencing it is in the built graph.
          // Sampling the raw render-target texture left the scene pass out of
          // the frame entirely — a black picture on both backends (2026-09-08).
          const [sw0, sh0] = lowResSize(w, h, scale); // THE LOW-RES SIZE LAW: the pass's own floor, not a round
          const easu = createEASUNode({ srcColor: scenePassColor, srcWidth: sw0, srcHeight: sh0, dstWidth: w, dstHeight: h, kernel: easuKernel });
          let rcas: RCASNodeResult | null = null;
          outputNode = easu.node;
          const sharp = config.upscale.sharpness ?? preset.defaultSharpness;
          if (sharp > 0) {
            rcas = createRCASNode({ srcColor: convertToTexture(easu.node as never) as never, srcWidth: w, srcHeight: h, sharpness: sharp });
            outputNode = rcas.node;
          }
          this._upscale = { scale, mode: 'fsr', easu, rcas, w, h };
          this.passes.set('upscale', { name: 'upscale', node: outputNode, enabled: true });
          Logger.info('PostProcessing', `FSR 1 upscale ${config.upscale.quality} (${scale}×: ${sw0}×${sh0} → ${w}×${h}, sharpness ${sharp.toFixed(2)})`);
        }
      }

      // ════════════════════════════════════════════════════════════════════
      // THE ONE EVALUATION (shader sweep 2026-10-06). The HDR chain below is ONE node graph evaluated per pixel by
      // whichever pass reads it. Three things made it run many times a frame:
      //   1. a stage that needs a TEXTURE (SSR, SSGI, DOF, lens flares, motion blur, bloom, SMAA) converted its input
      //      itself, but the chain AFTER it kept the expression — so every later render target re-evaluated
      //      everything above (high: the contact march, capsule, aerial, fog and AO three times a pixel);
      //   2. three r185's RTTNode updates per RENDER call: a target sampled by two of a node's internal passes (SMAA's
      //      edges + blend, DOF's blur + composite, the Kawase bloom's source + composite) RE-RENDERED its whole chain
      //      for each;
      //   3. those RTTs auto-sized to the DRAWING BUFFER while, under AA-first FSR and TAAU, the chain is consumed at
      //      the scene pass's resolution (×1.69–2.25 the pixels, re-sampled down again).
      // `chainTex` is the cure: one RTT per chain point, FRAME-updated (rendered once a frame, on first use), at the
      // chain's resolution, read by every later stage. 'legacy' keeps the pre-sweep graph exactly (the A/B).
      // ════════════════════════════════════════════════════════════════════
      const legacyChain = config?.chainTextures === 'legacy';
      const chainScale = !legacyChain && this._upscale && (fsrDeferred || this._upscale.mode === 'taau') ? 1 / this._upscale.scale : 1;
      this._chainScale = chainScale;
      this._chainLegacy = legacyChain;
      this._chainAtRender = !legacyChain && !!this._upscale && (fsrDeferred || this._upscale.mode === 'taau');
      let FRAME = 'frame'; // three's NodeUpdateType.FRAME (read guarded: a mocked namespace throws on a missing export)
      try { FRAME = (THREE as unknown as { NodeUpdateType?: { FRAME?: string } }).NodeUpdateType?.FRAME ?? FRAME; } catch { /* keep 'frame' */ }
      const onceAFrame = <T>(t: T): T => { if (!legacyChain && t) (t as { updateBeforeType?: string }).updateBeforeType = FRAME; return t; };
      const chainTex = (node: unknown): any => {
        const n = node as { isTextureNode?: boolean; isSampleNode?: boolean } | null;
        if (legacyChain || !n || n.isTextureNode || n.isSampleNode) return node;
        let t: { setResolutionScale?: (s: number) => void; renderTarget?: { dispose?: () => void } };
        try { t = onceAFrame(convertToTexture(node as never) as unknown as typeof t); } catch { return node; } // a stage that cannot take one converts as before
        if (!t) return node;
        if (chainScale !== 1) t.setResolutionScale?.(chainScale);
        this._chainRtts.push(t);
        return t;
      };

      // SURFACE BEFORE MEDIUM (shader gaps program, 2026-09-29 — the owner's report §14.4, and THE INERT FOG): contact and
      // capsule shadows occlude the SURFACE, so they run on the scene colour BEFORE the fog, the aerial haze and the
      // clouds veil it — T·(A·L) + S, not A·(T·L + S), which darkened the fog's own in-scattered light by the surface
      // occlusion. And the old order was the WebGL2 HIGH bug: the contact pass inlined the fog's composite (its own depth
      // reads) inside its ray-march loop, and the generated GLSL lost the fog entirely — a height fog forced red reached
      // no pixel with contact shadows on, every pixel with them off (atlas/client/fog_probe.html bisected it in seconds).
      // ════════════════════════════════════════════════════════════════════
      // 4b. Contact Shadows — screen-space ray-march, pre-tonemap multiplicative
      //     Darkens the scene colour where the depth buffer indicates occlusion
      //     along the primary directional light direction.
      //     Placed BEFORE God Rays and Bloom so that shadowed regions don't
      //     bleed bright halo artefacts into adjacent lit areas.
      // ════════════════════════════════════════════════════════════════════
      if (config?.contactShadows && config.contactShadows.enabled !== false) {
        try {
          // gaps wave (2026-09-28): the MRT view normal gates the shadow by N·L (0 on a face turned from the light — the
          // ~1 % self-contact); present whenever AO / SSR / SSGI / denoise asked for it, else the pass derives the normal
          // from its own depth (gaps wave round 2 — the gate no longer needs the MRT).
          const csResult = createContactShadowsNode(
            outputNode,
            scenePassDepth,
            this.camera,
            config.contactShadows,
            scenePassNormal ?? undefined,
          );
          outputNode = csResult.node;
          this._contactShadowsUniforms = csResult.uniforms;
          this.passes.set('contactShadows', { name: 'contactShadows', node: csResult.node, enabled: true });

          // Seed the light direction (the result used to be computed and thrown
          // away — the uniform stayed at (0,1,0)); render() refreshes it per frame.
          this._refreshContactShadowUniforms();

          Logger.info('PostProcessing', `Contact shadows pass enabled (samples=${csResult.config.sampleCount})`);
        } catch (err) {
          Logger.warn('PostProcessing', `Contact shadows pass failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 4c. Capsule Shadows — analytic character-proxy occlusion, after
      //     contact shadows on the same pre-tonemap colour.
      // ════════════════════════════════════════════════════════════════════
      if (config?.capsuleShadows && config.capsuleShadows.enabled !== false) {
        try {
          const cs = createCapsuleShadowsNode(
            outputNode, scenePassDepth, this.camera, config.capsuleShadows,
          );
          outputNode = cs.node;
          this._capsuleShadowsUniforms = cs.uniforms;
          this.passes.set('capsuleShadows', { name: 'capsuleShadows', node: cs.node, enabled: true });
          Logger.info('PostProcessing', 'Capsule shadows pass enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Capsule shadows failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 0. Volumetric clouds — depth-aware composite over SKY pixels only.
      //    Done FIRST (on the raw scene colour) so the clouds flow through the
      //    rest of the HDR chain (god rays / bloom / tone-map) exactly like the
      //    sky behind them. The cloud node ray-marches from the real camera
      //    position, so it is world-anchored + parallaxing; the sky mask
      //    (render/depth/skyMask.ts — the depth clear value, shared with
      //    GodRaysPass) keeps opaque geometry in front of the clouds.
      //        composited = sceneColor·(1 − a) + rgb        (over)
      //        final      = mix(sceneColor, composited, skyMask)
      // ════════════════════════════════════════════════════════════════════
      // Reusable composite: applied at slot 0 (default) or after AA
      // (clouds.afterAA — the TRAA interaction, see CloudsPassConfig).
      // Cloud raymarch at a FRACTION of the drawing buffer (perf 2026-09-08):
      // the RGBA cloud node renders once per frame into an RTT sized by
      // `clouds.resolutionScale` (0.5 = a quarter of the rays) and the
      // composite samples that texture bilinearly — a soft, low-frequency
      // volume upsampled cleanly; the temporal resolve / upscaler smooths it.
      let cloudSrc: { rgb: unknown; a: unknown } | null = null;
      if (config?.clouds?.node && config.clouds.enabled !== false) {
        const rs = config.clouds.resolutionScale;
        if (rs !== undefined && rs > 0 && rs < 1) {
          try {
            const { rtt } = await import('three/tsl');
            const n = config.clouds.node;
            const size0 = this.renderer.getDrawingBufferSize(new THREE.Vector2());
            const tex = onceAFrame(rtt(vec4((n.rgb as never), (n.a as never)) as never, Math.max(1, Math.round(size0.x * rs)), Math.max(1, Math.round(size0.y * rs))) as unknown as { setSize?: (w: number, h: number) => void; rgb: unknown; a: unknown });
            // RTT_AUTORESIZE_LAW (2026-09-25): three r185's RTTNode.autoResize is a GETTER (`width === null`) — an rtt() given a
            // size is already fixed (sized by _refreshCloudRtt at rs × the drawing buffer); ASSIGNING it throws in strict mode.
            this._cloudRtt = { node: tex, rs, w: size0.x, h: size0.y };
            cloudSrc = { rgb: tex.rgb, a: tex.a };
            Logger.info('PostProcessing', `Clouds raymarch at ${rs}× the drawing buffer (RTT)`);
          } catch (err) { Logger.warn('PostProcessing', `Cloud RTT unavailable, full-res raymarch: ${err}`); }
        }
      }
      const compositeClouds = (input: unknown): unknown => {
        const cloud = cloudSrc ?? config!.clouds!.node;
        // Sky mask: 1 where nothing was drawn, 0 on geometry — the helper also
        // does the `.r` reduction (a vec4 depth sample into step() is a WGSL /
        // GLSL codegen type mismatch). WHY the helper: the old
        // `step(0.999, depth)` is z ≈ near/0.001 = 50 m on Atlas's camera, so
        // the clouds composited over all geometry past 50 m instead of over
        // sky only. See render/depth/skyMask.ts.
        const mask = skyMask(scenePassDepth);
        const sceneColor = input as { mul: (n: unknown) => { add: (n: unknown) => unknown } };
        const oneMinusA = (cloud.a as { oneMinus: () => unknown }).oneMinus();
        const composited = sceneColor.mul(oneMinusA).add(cloud.rgb);
        return mix(input as never, composited as never, mask as never);
      };
      // gaps wave round 2: the late composite only where a temporal resolve will run (cloudsCompositeAfterAA) —
      // without one, `afterAA` put linear cloud radiance on display-referred colour (WebGL2 high/ultra, no TAAU there).
      const temporalResolve = !!scenePassVelocity && (this._upscale?.mode === 'taau' || !!config?.traa?.enabled);
      const cloudsLate = cloudsCompositeAfterAA(config?.clouds?.afterAA, temporalResolve);
      if (config?.clouds?.afterAA && !cloudsLate && config.clouds.node && config.clouds.enabled !== false) {
        Logger.info('PostProcessing', 'Clouds: afterAA asked, but no temporal resolve runs — composited at slot 0 (linear, before the tone map)');
      }
      let cloudsComposited = false;
      if (config?.clouds?.node && config.clouds.enabled !== false && !cloudsLate) {
        try {
          outputNode = compositeClouds(outputNode);
          cloudsComposited = true;
          this.passes.set('clouds', { name: 'clouds', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Volumetric clouds pass enabled (depth-aware sky composite)');
        } catch (err) {
          Logger.warn('PostProcessing', `Clouds pass failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 0b. Aerial perspective — froxel haze applied to GEOMETRY pixels on the
      //     raw HDR colour (sky pixels masked out: the sky-view LUT already
      //     carries full-path scattering). Closes the "baked but never
      //     composited" gap from the 2026-08-17 shader audit.
      // ════════════════════════════════════════════════════════════════════
      if (config?.aerial?.volume && config.aerial.enabled !== false) {
        try {
          const ap = createAerialCompositeNode(
            outputNode, scenePassDepth, this.camera, config.aerial,
          );
          outputNode = ap.node;
          this._aerialUniforms = ap.uniforms;
          this.passes.set('aerial', { name: 'aerial', node: ap.node, enabled: true });
          Logger.info('PostProcessing', 'Aerial-perspective composite enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Aerial composite failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 0c. Volumetric fog — analytic height fog + sun in-scatter + local
      //     volumes, on the HDR colour so the fog blooms/tone-maps with the
      //     scene it veils.
      // ════════════════════════════════════════════════════════════════════
      if (config?.volumetricFog && config.volumetricFog.enabled !== false) {
        try {
          const fog = createVolumetricFogNode(
            outputNode, scenePassDepth, this.camera, config.volumetricFog,
          );
          outputNode = fog.node;
          this._volumetricFogUniforms = fog.uniforms;
          this.passes.set('volumetricFog', { name: 'volumetricFog', node: fog.node, enabled: true });
          Logger.info('PostProcessing', 'Volumetric fog pass enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Volumetric fog failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 1. SSAO/AO — ao(depthNode, normalNode, camera) — multiplicative
      // ════════════════════════════════════════════════════════════════════
      if (config?.ao) {
        try {
          const { ao } = await import('three/examples/jsm/tsl/display/GTAONode.js');
          if (this._disposed) return;
          const aoPass = ao(scenePassDepth, scenePassNormal, this.camera);
          // Perf 2026-09-08: GTAO at a fraction of the render resolution. THE AO-GRID LAW (2026-09-27, the TAAU
          // verdict): three's GTAONode applies resolutionScale to the DRAWING BUFFER, so under a 1.5× upscale the tier's
          // "half-res" AO ran at 0.75 of the scene pass it reads — and GTAO's false occlusion on a flat plain at 50–75 m
          // grows with that ratio (a dark band under FSR and TAAU alike, none at native). Divide by the upscale.
          const aoBase = Math.max(0.25, Math.min(1, config.ao.resolutionScale ?? 1));
          (aoPass as { resolutionScale?: number }).resolutionScale = aoBase / (this._upscale?.scale ?? 1);
          this._ao = { node: aoPass as { resolutionScale?: number }, base: aoBase };
          // Render wave 1 (2026-09-17): the tier's radius/samples reach the node
          // (only resolutionScale did), and `intensity` is HOW MUCH of the
          // occlusion applies (mix toward 1), not a raw multiplier.
          if (config.ao.radius !== undefined && (aoPass as any).radius) (aoPass as any).radius.value = config.ao.radius;
          if (config.ao.samples !== undefined && (aoPass as any).samples) (aoPass as any).samples.value = config.ao.samples;
          const aoTex: any = (aoPass as any).getTextureNode();
          // THE REVERSED-Z STUDY (2026-09-29): three r185.1's GTAONode discards sky as `depth >= 1.0` — standard-only. Under
          // reversed depth the sky (cleared to 0) is SHADED (the depth bed read a fully occluded sky on one backend). The
          // occlusion applies only where geometry drew (`geometryMask` follows the convention); under standard depth GTAO
          // already writes 1 on the sky, so this is the same picture.
          const aoTerm = mix(float(1), aoTex.r, float(Math.max(0, Math.min(1, config.ao.intensity ?? 1))).mul(geometryMask(scenePassDepth)));
          outputNode = vec4((outputNode as any).rgb.mul(aoTerm), (outputNode as any).a);
          this.passes.set('ao', { name: 'ao', node: aoPass, enabled: true });
          Logger.info('PostProcessing', 'AO (GTAO) pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'AO pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 2. SSGI — ssgi(beautyNode, depthNode, normalNode, camera)
      // ════════════════════════════════════════════════════════════════════
      if (config?.ssgi) {
        try {
          const { ssgi } = await import('three/examples/jsm/tsl/display/SSGINode.js');
          if (this._disposed) return;
          const beauty = chainTex(outputNode); // THE ONE EVALUATION: SSGI and the composite below read one copy of the chain
          const ssgiPass = ssgi(beauty, scenePassDepth, scenePassNormal, this.camera as any);
          // three r185 SSGINode: getAONode()/getGINode(); the example composites
          // colour·ao + diffuse·gi — the scene colour stands in for the diffuse MRT.
          if (config.ssgi.sliceCount !== undefined && (ssgiPass as any).sliceCount) (ssgiPass as any).sliceCount.value = config.ssgi.sliceCount;
          if (config.ssgi.stepCount !== undefined && (ssgiPass as any).stepCount) (ssgiPass as any).stepCount.value = config.ssgi.stepCount;
          // Temporal SSGI jitters its slices for a temporal resolve to average; without one (WebGL2's spatial TAAU
          // fallback, an SMAA tier) the jitter only shimmers — so it follows the resolve that actually runs (2026-09-25).
          // An UNSET `temporal` is not three's default (true): a menu-added SSGI on a spatial tier jittered unresolved.
          // The AA slot below re-checks it against the resolve actually BUILT (`this._temporal`).
          (ssgiPass as any).useTemporalFiltering = config.ssgi.temporal !== false && (taauWanted || !!config.traa?.enabled);
          const aoN = (ssgiPass as any).getAONode(), giN = (ssgiPass as any).getGINode();
          // geometry only (the reversed-Z study: SSGINode discards sky as `depth >= 1.0`, standard-only — see the AO note)
          const ssgiGeo = geometryMask(scenePassDepth);
          const aoTerm = mix(float(1), aoN.r, float(config.ssgi.aoStrength ?? 0.5).mul(ssgiGeo)); // full-strength SSGI AO halved a potato frame's brightness (probe 2026-09-09)
          const lit = (beauty as any).rgb.mul(aoTerm).add((beauty as any).rgb.mul(giN.rgb).mul(float(config.ssgi.intensity ?? 1).mul(ssgiGeo)));
          outputNode = vec4(lit, (beauty as any).a);
          this.passes.set('ssgi', { name: 'ssgi', node: ssgiPass, enabled: true });
          Logger.info('PostProcessing', 'SSGI pass enabled');
        } catch (e) {
          Logger.warn('PostProcessing', `SSGI pass not available — skipping: ${String(e).slice(0, 160)}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 3. SSR — ssr(color, depth, normal, metalness, roughness, camera)
      // ════════════════════════════════════════════════════════════════════
      if (config?.ssr) {
        if (!ssrRuns) {
          // `ssr.webgl2: false` on the WebGL2 backend — skipped, and its targets were never requested.
          Logger.info('PostProcessing', 'SSR switched off on WebGL2 (ssr.webgl2 = false)');
          skip('ssr', 'switched off on WebGL2 (ssr.webgl2 = false)');
        } else if (!scenePassNormal || !scenePassMetalness) {
          Logger.warn('PostProcessing', 'SSR requires MRT (normal + metalness) — skipping. Set up MRT on the scene pass.');
          skip('ssr', this._manifest.dropped.some((d) => d.startsWith('SSR')) ? 'dropped by the colour-attachment budget' : 'no normal / metalrough target');
        } else {
          try {
            // THE VENDORED NODE (gaps wave round 2): three r185.1's SSRNode emits `max(int(trunc(x)), 1.0)` for its
            // step count — invalid GLSL, the ONE compile error that kept SSR off WebGL2 since 2026-09-09
            // (verify/ssr-bed-probe.mjs captured it). The copy fixes that line and nothing else (`// ENGINEX:` marks).
            const { ssr } = await import('./vendor/SSRNodeCompat.js');
            if (this._disposed) return;
            // three r185: ssr(color, depth, normal, { metalnessNode, roughnessNode, camera }) — the
            // per-pixel PBR inputs ride the options object and the result is ADDED to the
            // scene colour (the r185 example: scenePassColor.add(ssrPass.rgb)). The older
            // positional call threw here and the pass was silently "not available".
            // SSRNode samples its colour input (`this.colorNode.sample`): hand it a texture NODE.
            const ssrIn = legacyChain ? outputNode : chainTex(outputNode); // THE ONE EVALUATION (legacy: the stage converts, the chain re-evaluates below)
            const ssrPass: any = (ssr as any)(convertToTexture(ssrIn as never), scenePassDepth, scenePassNormal, {
              metalnessNode: scenePassMetalness,
              roughnessNode: scenePassRoughness,
              camera: this.camera,
            });
            if (config.ssr.intensity !== undefined && ssrPass.intensity) ssrPass.intensity.value = config.ssr.intensity;
            if (config.ssr.maxDistance !== undefined && ssrPass.maxDistance) ssrPass.maxDistance.value = config.ssr.maxDistance;
            if (config.ssr.thickness !== undefined && ssrPass.thickness) ssrPass.thickness.value = config.ssr.thickness;
            // THE AO-GRID LAW, applied to SSR (sky/post P2, 2026-09-27 — the materials agent's hand-off): three's SSRNode
            // sizes its march target from the DRAWING BUFFER × resolutionScale (SSRNode.updateBefore → setSize), so
            // under a 1.5× upscale the tier's "half-res" SSR ran at 0.75 of the scene pass it reads (and a full-res SSR
            // at 1.5× it: 2.25× the rays, marching an upsampled depth). The scale is the tier's fraction of the RENDER
            // resolution ÷ the upscale ratio, re-aimed by setRenderScale like the AO's.
            const ssrBase = Math.max(0.25, Math.min(1, config.ssr.resolutionScale ?? 1));
            ssrPass.resolutionScale = ssrBase / (this._upscale?.scale ?? 1);
            this._ssr = { node: ssrPass as { resolutionScale?: number }, base: ssrBase };
            // SSR OWNERSHIP (render wave 1, 2026-09-17): a HIT replaces the beauty's
            // indirect specular by metalness instead of stacking on it — the r185
            // example adds, and a metal or a puddle then reflected twice (the sky
            // PMREM once in the scene pass, the ray once more). The node's alpha is
            // the hit's world distance (0 on a miss). post/ssrComposite.ts is the
            // CPU mirror the unit test pins.
            // The node's blurred alpha is the hit's CONFIDENCE (vendored change, 2026-09-29) — replace only that
            // fraction of the metal's specular (post/ssrComposite.ts: the dark rim was a binary step here).
            const ssrTex: any = ssrPass.getTextureNode();
            const ssrW = clamp(ssrTex.a, 0, 1).mul(clamp(float(scenePassMetalness as never), 0, 1)).mul(float(SSR_REPLACE));
            const ssrRgb = (ssrIn as any).rgb.mul(float(1).sub(ssrW)).add(ssrTex.rgb);
            outputNode = vec4(ssrRgb, (ssrIn as any).a);
            this.passes.set('ssr', { name: 'ssr', node: ssrPass, enabled: true });
            Logger.info('PostProcessing', 'SSR pass enabled');
          } catch (e) {
            Logger.warn('PostProcessing', `SSR pass not available — skipping: ${String(e).slice(0, 160)}`);
            skip('ssr', `build failed: ${String(e).slice(0, 120)}`);
          }
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 4. Denoise — denoise(textureNode, depthNode, normalNode, camera)
      // ════════════════════════════════════════════════════════════════════
      if (config?.denoise) {
        try {
          const { denoise } = await import('three/examples/jsm/tsl/display/DenoiseNode.js');
          if (this._disposed) return;
          const denoisePass = denoise(chainTex(outputNode), scenePassDepth, scenePassNormal, this.camera);
          outputNode = denoisePass;
          this.passes.set('denoise', { name: 'denoise', node: denoisePass, enabled: true });
          Logger.info('PostProcessing', 'Denoise pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Denoise pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 5. God Rays — additive, composited before bloom so rays get bloom'd
      // ════════════════════════════════════════════════════════════════════
      if (config?.godRays && config.godRays.enabled !== false) {
        try {
          const godRaysResult = createGodRaysNode(
            scenePassColor, scenePassDepth, config.godRays,
          );
          outputNode = (outputNode as any).add(godRaysResult.node);
          this._godRays = godRaysResult;
          this.passes.set('godRays', { name: 'godRays', node: godRaysResult.node, enabled: true });
          Logger.info('PostProcessing', 'God rays pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'God rays pass not available');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 6. DOF — dof(node, viewZNode, focusDistance, focalLength, bokehScale)
      // ════════════════════════════════════════════════════════════════════
      if (config?.dof) {
        try {
          const { dof } = await import('three/examples/jsm/tsl/display/DepthOfFieldNode.js');
          if (this._disposed) return;
          // Three.js DOF expects a view-space Z node (negative, in metres),
          // NOT raw 0..1 device depth. Passing scenePassDepth directly made
          // the circle-of-confusion radius wrong at every distance except
          // exactly the focus plane. Convert via the matching helper for
          // the camera projection type.
          // Use `uniform()` (not `float()`) so changes to the camera's
          // near/far (zoom, cutscene swap, setCamera()) propagate. `float()`
          // creates a CONSTANT TSL node that bakes the construction-time
          // values into the shader — same class of bug as ContactShadowsNode
          // pre-pass-1. Uniforms are stored on the instance so setCamera()
          // and the per-frame render hook can refresh `.value`.
          const cam = this.camera as THREE.PerspectiveCamera & THREE.OrthographicCamera;
          const isOrtho = (this.camera as { isOrthographicCamera?: boolean }).isOrthographicCamera === true;
          this._dofNearUniform = uniform(cam.near);
          this._dofFarUniform = uniform(cam.far);
          const viewZNode = isOrtho
            ? orthographicDepthToViewZ(scenePassDepth as never, this._dofNearUniform, this._dofFarUniform)
            : perspectiveDepthToViewZ(scenePassDepth as never, this._dofNearUniform, this._dofFarUniform);
          if (config.dof.bokeh === 'hex') {
            // The custom hex-aperture bokeh (audit: BokehDofNode was never
            // wired). bokehScale maps onto the CoC pixel cap.
            //
            // The hex path's model is `coc_px = cocScale·(1 − focusZ/z)`, a
            // PIXEL scale, where three's standard path takes `focalLength` as a
            // world-unit focus RANGE. One config field, two incompatible
            // meanings — the old `focalLength · 200` only produced a sane pixel
            // scale because `focalLength` was being set as if it were a lens
            // length in metres. Derive the pixel scale from the range instead,
            // inversely: a wide focus range means less blur (2026-09-14).
            const { createBokehDofNode } = await import('./dof/BokehDofNode.js');
            const { createBokehDofConfig } = await import('./dof/BokehDofConfig.js');
            if (this._disposed) return;
            const focusRange = Math.max(0.5, config.dof.focalLength ?? 40);
            const hexCfg = createBokehDofConfig({
              focusDistance: config.dof.focusDistance ?? 10,
              cocScale: Math.max(2, Math.min(32, 600 / focusRange)),
              maxCocPixels: (config.dof.bokehScale ?? 5) * 3.2,
            });
            // Earlier passes replace outputNode with an expression node; the
            // bokeh samples it with texture(), which throws on anything but a
            // Texture/TextureNode — render it to a texture first.
            const hex = createBokehDofNode({
              sceneColor: (legacyChain ? convertToTexture(outputNode as never) : chainTex(outputNode)) as never, viewZNode, config: hexCfg,
            });
            outputNode = hex.node;
            this.passes.set('dof', { name: 'dof', node: hex.node, enabled: true });
            Logger.info('PostProcessing', 'DOF pass enabled (hex bokeh)');
          } else {
            // LIVE lens inputs (R4): uniforms, so an autofocus / an aperture change never rebuilds the chain.
            const focus = config.dof.focusDistance ?? 10;
            let range = config.dof.focalLength ?? 40, bokeh = config.dof.bokehScale ?? 5;
            if (config.dof.aperture) {
              const { physicalDofParams } = await import('./dof/PhysicalDof.js');
              const p = physicalDofParams(focus, config.dof.aperture, this._drawingBufferSize().x);
              range = p.focusRange; bokeh = p.bokehScale;
            }
            // three's DepthOfFieldNode sizes itself from its INPUT and steps its bokeh in INPUT pixels: a chain-resolution
            // input (THE ONE EVALUATION) takes the bokeh × the chain scale so the blur covers the same OUTPUT pixels.
            this._dofBokehK = legacyChain ? 1 : chainScale;
            this._dofFocus = uniform(focus); this._dofRange = uniform(range); this._dofBokeh = uniform(bokeh * this._dofBokehK);
            const dofPass = (dof as any)(
              chainTex(outputNode),
              viewZNode,
              this._dofFocus,
              this._dofRange, // focus RANGE in world units — see DOFConfig
              this._dofBokeh,
            );
            outputNode = dofPass;
            this.passes.set('dof', { name: 'dof', node: dofPass, enabled: true });
            Logger.info('PostProcessing', 'DOF pass enabled');
          }
        } catch {
          Logger.warn('PostProcessing', 'DOF pass not available — skipping');
        }
      }

      // ════════════════════════════════════════════════════════════════════
      // 10b. Lens flares — pseudo (screen-space) flares from the frame's bright
      //      pixels; sampled through the texture NODE (the pass law).
      // ════════════════════════════════════════════════════════════════════
      this._lensFlare = null;
      if (config?.lensFlares && config.lensFlares.enabled !== false) {
        try {
          // three r185's first-party screen-space flare (ghosts pivoting around
          // the centre from the frame's bright pixels — the pseudo-lens-flare
          // family). The engine's own LensFlareNode emitted an invalid GLSL
          // literal on WebGL2 (2026-09-09 probe), so the maintained node is the
          // wiring; the engine config maps onto its params.
          const { lensflare } = await import('three/examples/jsm/tsl/display/LensflareNode.js');
          if (this._disposed) return;
          const cfg = createLensFlareConfig(config.lensFlares);
          const flareIn = chainTex(outputNode); // THE ONE EVALUATION: the flare and the add below read one copy
          const flare: any = (lensflare as any)(flareIn, {
            threshold: cfg.brightThreshold,
            ghostSamples: 4,
            ghostSpacing: 0.25,
            ghostAttenuationFactor: 25,
            ghostTint: new THREE.Color(cfg.streakColor.r, cfg.streakColor.g, cfg.streakColor.b),
            downSampleRatio: 4,
          });
          const gain = cfg.ghostIntensity;
          outputNode = (flareIn as any).add(flare.rgb.mul(gain));
          this._lensFlare = { node: flare, uniforms: { threshold: flare.threshold ?? null, gain } } as unknown as LensFlareNodeResult;
          this.passes.set('lensFlares', { name: 'lensFlares', node: flare, enabled: true });
          Logger.info('PostProcessing', 'Lens flares (screen-space pseudo flare) enabled');
        } catch (e) {
          Logger.warn('PostProcessing', `Lens flares unavailable: ${String(e).slice(0, 160)}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 7. Motion Blur — motionBlur(inputNode, velocity, numSamples)
      // ════════════════════════════════════════════════════════════════════
      if (config?.motionBlur) {
        if (!scenePassVelocity) {
          Logger.warn('PostProcessing', 'Motion blur requires velocity MRT — skipping. Set up MRT with velocity output.');
          skip('motionBlur', this._manifest.dropped.some((d) => d.startsWith('velocity')) ? 'velocity dropped by the colour-attachment budget' : 'no velocity target');
        } else {
          try {
            // WHY not three's stock motionBlur(): it samples only along the
            // pixel's OWN velocity, applies NDC motion as UV (2× too long),
            // never clamps (a camera flick = full-screen streaks), has no
            // depth awareness and a fixed tap lattice — the "glitchy" look.
            // The reconstruction filter (tile-max → neighbour-max → jittered
            // depth-weighted gather, frame-rate-normalised shutter) is the
            // McGuire 2012 / Jimenez 2014 standard.
            const { MotionBlurNode } = await import('./post/MotionBlurNode.js');
            if (this._disposed) return;
            const mb = config.motionBlur;
            const mbPass = new MotionBlurNode(chainTex(outputNode), scenePassVelocity, scenePassDepth ?? null, this.camera, {
              samples: mb.samples ?? 16, intensity: mb.intensity ?? 1, maxBlurPx: mb.maxBlurPx ?? 48,
              tileSize: mb.tileSize ?? 20, depthAware: mb.depthAware ?? true,
              shutterFraction: mb.shutterFraction, cameraWeight: mb.cameraWeight, objectWeight: mb.objectWeight,
            });
            outputNode = mbPass as unknown as typeof outputNode;
            this.passes.set('motionBlur', { name: 'motionBlur', node: mbPass, enabled: true });
            Logger.info('PostProcessing', `Motion blur pass enabled (reconstruction filter: ${mb.samples ?? 16} taps, intensity ${mb.intensity ?? 1}, max ${mb.maxBlurPx ?? 48} px)`);
          } catch (err) {
            Logger.warn('PostProcessing', `Motion blur pass not available — skipping (${err})`);
          }
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 8. Bloom — bloom(node, strength, radius, threshold) — additive
      // ════════════════════════════════════════════════════════════════════
      if (config?.bloom && config.bloom.mode === 'kawase') {
        // Dual-Kawase bloom (stage 4): rtt levels sampled through their nodes.
        try {
          const { dualKawaseBloom } = await import('./post/DualKawaseBloom.js');
          if (this._disposed) return;
          const size = this._drawingBufferSize();
          const lerp = config.bloom.composite === 'lerp';
          const passes = config.bloom.passes ?? (lerp ? 5 : 2);
          const bloomIn = chainTex(outputNode); // THE ONE EVALUATION: the pyramid's source and the composite read one copy
          const kb = dualKawaseBloom(bloomIn, {
            width: size.x, height: size.y,
            onceAFrame: !legacyChain,
            strength: config.bloom.strength ?? (lerp ? 0.04 : 0.5),
            threshold: config.bloom.threshold ?? 0.8,
            radius: config.bloom.radius ?? 1,
            passes,
            energyConserving: lerp,
          });
          // 'lerp': the node is (blur − scene)·strength, so the add below IS mix(scene, blur, strength).
          outputNode = (bloomIn as any).add(kb.node);
          this.passes.set('bloom', { name: 'bloom', node: kb.node, enabled: true });
          this._kawase = kb;
          Logger.info('PostProcessing', `Bloom: dual-Kawase (${passes} down/up pairs${lerp ? ', energy-conserving lerp' : ''}) at ${size.x}×${size.y}`);
        } catch (err) {
          Logger.warn('PostProcessing', `Dual-Kawase bloom not available: ${err}`);
        }
      } else if (config?.bloom) {
        try {
          const { bloom } = await import('three/examples/jsm/tsl/display/BloomNode.js');
          if (this._disposed) return;
          const bloomIn = chainTex(outputNode);
          const bloomPass = bloom(
            bloomIn,
            config.bloom.strength ?? 1.5,
            config.bloom.radius ?? 0.4,
            config.bloom.threshold ?? 0.8,
          );
          outputNode = (bloomIn as any).add(bloomPass);
          this.passes.set('bloom', { name: 'bloom', node: bloomPass, enabled: true });
        } catch {
          Logger.warn('PostProcessing', 'Bloom pass not available');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 9. Anamorphic — anamorphic(node, threshold, scale, samples) — additive
      // ════════════════════════════════════════════════════════════════════
      if (config?.anamorphic) {
        try {
          // r185 removed AnamorphicNode upstream — vendored copy (see vendor/AnamorphicNode.js).
          const { anamorphic } = await import('./vendor/AnamorphicNode.js');
          if (this._disposed) return;
          const anaIn = chainTex(outputNode);
          const anamorphicPass = (anamorphic as any)(
            anaIn,
            config.anamorphic.threshold ?? 0.9,
            config.anamorphic.scale ?? 3,
            config.anamorphic.samples ?? 32,
          );
          outputNode = (anaIn as any).add(anamorphicPass);
          this.passes.set('anamorphic', { name: 'anamorphic', node: anamorphicPass, enabled: true });
          Logger.info('PostProcessing', 'Anamorphic pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Anamorphic pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 10. After Image — afterImage(node, damp) — temporal trail
      // ════════════════════════════════════════════════════════════════════
      if (config?.afterImage) {
        try {
          const { afterImage } = await import('three/examples/jsm/tsl/display/AfterImageNode.js');
          if (this._disposed) return;
          const aiPass = afterImage(outputNode as any, config.afterImage.damp ?? 0.96);
          outputNode = aiPass;
          this.passes.set('afterImage', { name: 'afterImage', node: aiPass, enabled: true });
          Logger.info('PostProcessing', 'After image pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'After image pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 11. Outline — outline(scene, camera, selectedObjects) — additive
      // ════════════════════════════════════════════════════════════════════
      if (config?.outline) {
        try {
          const { outline } = await import('three/examples/jsm/tsl/display/OutlineNode.js');
          if (this._disposed) return;
          const outlinePass = (outline as any)(this.scene, this.camera, config.outline.selectedObjects);
          outputNode = (outputNode as any).add(outlinePass);
          this.passes.set('outline', { name: 'outline', node: outlinePass, enabled: true });
          Logger.info('PostProcessing', 'Outline pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Outline pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 12. Film — film(inputNode, intensityNode) — grain overlay
      // ════════════════════════════════════════════════════════════════════
      if (config?.film && config.film.mode !== 'luma') {
        try {
          const { film } = await import('three/examples/jsm/tsl/display/FilmNode.js');
          if (this._disposed) return;
          const filmPass = (film as any)(outputNode, config.film.intensity ?? 1);
          outputNode = filmPass;
          this.passes.set('film', { name: 'film', node: filmPass, enabled: true });
          Logger.info('PostProcessing', 'Film grain pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Film pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 13. Dot Screen — dotScreen(node, angle, scale) — halftone
      // ════════════════════════════════════════════════════════════════════
      if (config?.dotScreen) {
        try {
          const { dotScreen } = await import('three/examples/jsm/tsl/display/DotScreenNode.js');
          if (this._disposed) return;
          const dotPass = (dotScreen as any)(outputNode, config.dotScreen.angle ?? 1.57, config.dotScreen.scale ?? 1);
          outputNode = dotPass;
          this.passes.set('dotScreen', { name: 'dotScreen', node: dotPass, enabled: true });
          Logger.info('PostProcessing', 'Dot screen pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Dot screen pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 14. RGB Shift — rgbShift(node, amount, angle) — color fringing
      // ════════════════════════════════════════════════════════════════════
      if (config?.rgbShift) {
        try {
          const { rgbShift } = await import('three/examples/jsm/tsl/display/RGBShiftNode.js');
          if (this._disposed) return;
          const rgbPass = (rgbShift as any)(outputNode, config.rgbShift.amount ?? 0.005, config.rgbShift.angle ?? 0);
          outputNode = rgbPass;
          this.passes.set('rgbShift', { name: 'rgbShift', node: rgbPass, enabled: true });
          Logger.info('PostProcessing', 'RGB shift pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'RGB shift pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 15. Chromatic Aberration — chromaticAberration(node, strength, center, scale)
      // ════════════════════════════════════════════════════════════════════
      // (Chromatic aberration moved BELOW the display boundary, after AA and the
      // upscale — AMD's order, §10 R11, lighting agent 2026-09-25: see 24d2.)
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 16. Sepia — sepia(color) — warm color grading
      // ════════════════════════════════════════════════════════════════════
      if (config?.sepia) {
        try {
          const { sepia } = await import('three/examples/jsm/tsl/display/Sepia.js');
          if (this._disposed) return;
          outputNode = sepia(outputNode as any);
          this.passes.set('sepia', { name: 'sepia', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Sepia pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Sepia pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 17. Grayscale — grayscale(color) — desaturation
      // ════════════════════════════════════════════════════════════════════
      if (config?.grayscale) {
        try {
          const tsl = await import('three/tsl');
          if (this._disposed) return;
          if (typeof (tsl as any).grayscale === 'function') {
            outputNode = (tsl as any).grayscale(outputNode as any);
          } else if (typeof (tsl as any).luminance === 'function') {
            // Fallback: luminance is a common grayscale conversion
            outputNode = (tsl as any).luminance(outputNode as any);
          }
          this.passes.set('grayscale', { name: 'grayscale', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Grayscale pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Grayscale pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 17b. Parametric colour grade — lift/gamma/gain + saturation, applied
      //      BEFORE the 3D LUT so a baked LUT grades on top of the parametric
      //      base (the conventional grade → LUT order).
      // ════════════════════════════════════════════════════════════════════
      // (The parametric grade and the 3D LUT moved BELOW the display boundary —
      // a LUT is authored on display values, and fed HDR its lookup clamped every
      // highlight to the cube's corner colour. Lighting agent 2026-09-25, §10 R2:
      // see 24d1.)
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 19. Sobel — sobel(node) — edge detection
      // ════════════════════════════════════════════════════════════════════
      if (config?.sobel) {
        try {
          const { sobel } = await import('three/examples/jsm/tsl/display/SobelOperatorNode.js');
          if (this._disposed) return;
          outputNode = sobel(outputNode as any);
          this.passes.set('sobel', { name: 'sobel', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Sobel edge detection pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Sobel pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 20. Gaussian Blur — gaussianBlur(node, direction, sigma, options)
      // ════════════════════════════════════════════════════════════════════
      if (config?.gaussianBlur) {
        try {
          const { gaussianBlur } = await import('three/examples/jsm/tsl/display/GaussianBlurNode.js');
          if (this._disposed) return;
          const gbPass = (gaussianBlur as any)(
            outputNode,
            null, // direction — null for both H+V passes
            config.gaussianBlur.sigma ?? 4,
            { resolution: config.gaussianBlur.resolutionScale ?? 1 },
          );
          outputNode = gbPass;
          this.passes.set('gaussianBlur', { name: 'gaussianBlur', node: gbPass, enabled: true });
          Logger.info('PostProcessing', 'Gaussian blur pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Gaussian blur pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 21. Box Blur — boxBlur(textureNode, options)
      // ════════════════════════════════════════════════════════════════════
      if (config?.boxBlur) {
        try {
          // boxBlur may be in three/tsl core or in display addons
          let boxBlurFn: any = null;
          try {
            const tsl = await import('three/tsl');
            boxBlurFn = (tsl as any).boxBlur;
          } catch { /* not in core */ }
          if (!boxBlurFn) {
            try {
              const mod = await import('three/examples/jsm/tsl/display/GaussianBlurNode.js');
              boxBlurFn = (mod as any).boxBlur;
            } catch { /* not in GaussianBlurNode */ }
          }
          if (this._disposed) return;
          if (boxBlurFn) {
            outputNode = boxBlurFn(outputNode as any, {});
            this.passes.set('boxBlur', { name: 'boxBlur', node: outputNode, enabled: true });
            Logger.info('PostProcessing', 'Box blur pass enabled');
          } else {
            Logger.warn('PostProcessing', 'Box blur function not found — skipping');
          }
        } catch {
          Logger.warn('PostProcessing', 'Box blur pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 22. Hash Blur — hashBlur(textureNode, amount, options)
      // ════════════════════════════════════════════════════════════════════
      if (config?.hashBlur) {
        try {
          const { hashBlur } = await import('three/examples/jsm/tsl/display/hashBlur.js');
          if (this._disposed) return;
          outputNode = (hashBlur as any)(outputNode, config.hashBlur.amount ?? 0.1, {});
          this.passes.set('hashBlur', { name: 'hashBlur', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Hash blur pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Hash blur pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 23. Transition — transition(nodeA, nodeB, mixTexture, mixRatio, threshold, useTexture)
      // ════════════════════════════════════════════════════════════════════
      if (config?.transition) {
        try {
          const { transition } = await import('three/examples/jsm/tsl/display/TransitionNode.js');
          if (this._disposed) return;
          const passB = pass(config.transition.sceneB, config.transition.cameraB);
          this._ownedPassNodes.push(passB); // owns a render target — freed in dispose()
          const colorB = (passB as any).getTextureNode('output');
          let mixTextureNode: any = null;
          if (config.transition.mixTexture) {
            // Create a texture node from the provided mix texture
            const tsl = await import('three/tsl');
            if ((tsl as any).texture) {
              mixTextureNode = (tsl as any).texture(config.transition.mixTexture);
            }
          }
          const transNode = (transition as any)(
            outputNode,
            colorB,
            mixTextureNode,
            config.transition.mixRatio ?? 0,
            config.transition.threshold ?? 0.1,
            config.transition.useTexture ? 1 : 0,
          );
          outputNode = transNode;
          this.passes.set('transition', { name: 'transition', node: transNode, enabled: true });
          Logger.info('PostProcessing', 'Transition pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Transition pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24. Anti-Aliasing — TRAA > SMAA > FXAA (mutually exclusive, must be LAST)
      //     `quality` (2|4|8|16|32) maps to ITERATED edge-resolve passes —
      //     log2(q) applications (2×=1 … 32×=5). This is the only real
      //     quality axis these post-AA algorithms expose (three's nodes have
      //     no internal presets, and WebGPU hardware MSAA caps at 4×): each
      //     extra pass genuinely resolves more residual edge aliasing at the
      //     cost of added softness + a full-screen pass. TRAA is temporal and
      //     must run exactly once — quality ≥ 8 chains ONE FXAA behind the
      //     resolve as the spatial cleanup step.
      // ════════════════════════════════════════════════════════════════════
      let aaApplied = false;
      // ── THE DISPLAY TRANSFORM BOUNDARY (2026-09-15) ────────────────────
      // Tone mapping + the output colour space used to run AFTER the whole
      // chain: three's RenderPipeline does it for `outputNode` when
      // `outputColorTransform` is true (the default), so the LUT, sharpen,
      // vignette, film grain, chromatic aberration and the colourblind
      // transform were all operating on unmapped linear HDR — values well
      // above 1 — even though each of their comments says "display-referred".
      // A LUT in particular is authored against display values; feeding it raw
      // radiance silently samples the wrong part of the cube.
      //
      // So the transform is applied HERE instead, once, at the boundary the
      // passes were written for: after the HDR chain and the anti-aliasing,
      // before the display-referred finishing stages. Everything upstream
      // stays in linear HDR, which is what bloom, DOF, god rays and the
      // atmosphere all assume.
      //
      // FXAA is the exception that proves the rule and moves across the line
      // WITH the transform: three documents it as needing tone-mapped,
      // output-encoded input, while SMAA's own header says the opposite
      // ("should be applied before converting colors to sRGB"). So SMAA stays
      // above the boundary and FXAA sits just below it.
      let displayTransformed = false;
      // The Purkinje shift (2026-09-27): linear, immediately before the ONE display transform — every AA / upscale
      // path passes through here, and the display-referred grade + LUT follow it.
      this._purkinje = config?.purkinje && config.purkinje.enabled !== false ? createPurkinjeUniforms() : null;
      const toDisplay = (node0: unknown): unknown => {
        if (displayTransformed) return node0;
        displayTransformed = true;
        const node = this._purkinje ? applyPurkinje(node0, this._purkinje) : node0;
        if (this._purkinje) this.passes.set('purkinje', { name: 'purkinje', node, enabled: true });
        // A custom operator (TonemapConfig.customNode, lighting agent 2026-09-25): exposure here, three's curve off.
        const custom = config?.tonemap?.customNode;
        const out = custom
          ? renderOutput(vec4(custom(vec4(node as never).rgb.mul(toneMappingExposure)) as never, vec4(node as never).a) as never, THREE.NoToneMapping)
          : renderOutput(node as never);
        // Registered HERE, not at the call site below, so the pass list's order
        // is the GRAPH's order even when FXAA pulls the boundary earlier.
        this.passes.set('outputTransform', { name: 'outputTransform', node: out, enabled: true });
        return out;
      };
      const aaIters = (q: number | undefined): number =>
        Math.max(1, Math.min(5, Math.round(Math.log2(Math.max(2, q ?? 2)))));
      /**
       * `aaQuality` (an FXAA-style 2/4/8 "x" number) → an SMAA PRESET.
       * SMAA quality is threshold + search length, not sample count, so the
       * mapping is by intent rather than arithmetic: x2 is the paper's medium
       * (three's built-in behaviour and the old effective default), x4 high,
       * x8 ultra. Anything below 2 takes the cheap preset.
       */
      const smaaPresetFor = (q: number | undefined): 'low' | 'medium' | 'high' | 'ultra' =>
        q === undefined ? 'medium' : q >= 8 ? 'ultra' : q >= 4 ? 'high' : q >= 2 ? 'medium' : 'low';

      // 24-pre. FSR with AA FIRST (R3, AMD's order): tone-map once here, render the display-referred chain
      //         into a RENDER-resolution target, anti-alias it there (SMAA sized from its input / FXAA inside
      //         its own render-res target), then EASU + RCAS reconstruct the output. EASU sees a perceptual,
      //         anti-aliased image — the input the kernel was designed for.
      if (!aaApplied && fsrDeferred && this._upscale) {
        try {
          const { rtt } = await import('three/tsl');
          if (this._disposed) return;
          const u = this._upscale;
          const [rw, rh] = lowResSize(u.w, u.h, u.scale); // THE LOW-RES SIZE LAW: exactly the scene pass (no resample)
          // explicit size → not auto-resized (the RTT_AUTORESIZE_LAW: assigning three's autoResize getter threw and dropped the EASU)
          const lowRes = (n: unknown): unknown => { const t = onceAFrame(rtt(n as never, rw, rh)); this._lowResRtts.push(t as never); return t; };
          const disp = lowRes(toDisplay(outputNode));
          let aaTex: unknown;
          if (config?.smaa?.enabled) {
            const { smaa } = await import('./vendor/SMAAQualityNode.js');
            if (this._disposed) return;
            const s2 = smaa(disp, smaaPresetFor(config.smaa.quality), { sizeFromInput: true });
            aaTex = (s2 as { getTextureNode(): unknown }).getTextureNode(); // the PASS LAW: sample its texture NODE (it pulls the SMAA passes into the frame)
            this.passes.set('smaa', { name: 'smaa', node: s2, enabled: true });
          } else {
            const { fxaa } = await import('three/examples/jsm/tsl/display/FXAANode.js');
            if (this._disposed) return;
            aaTex = lowRes(fxaa(disp as never));
            this.passes.set('fxaa', { name: 'fxaa', node: aaTex, enabled: true });
          }
          const easu = createEASUNode({ srcColor: aaTex, srcWidth: rw, srcHeight: rh, dstWidth: u.w, dstHeight: u.h, kernel: easuKernel });
          outputNode = easu.node;
          const sharp = config?.upscale?.sharpness ?? FSR_PRESETS[config!.upscale!.quality].defaultSharpness;
          let rcas: RCASNodeResult | null = null;
          if (sharp > 0) { const easuTex = onceAFrame(convertToTexture(easu.node as never)); if (!legacyChain) this._outRtts.push(easuTex as never); rcas = createRCASNode({ srcColor: easuTex as never, srcWidth: u.w, srcHeight: u.h, sharpness: sharp }); outputNode = rcas.node; }
          u.easu = easu; u.rcas = rcas;
          this.passes.set('upscale', { name: 'upscale', node: outputNode, enabled: true });
          aaApplied = true;
          Logger.info('PostProcessing', `AA-first FSR: ${config?.smaa?.enabled ? 'SMAA' : 'FXAA'} at ${rw}×${rh} → EASU (${easuKernel}) → ${u.w}×${u.h}${rcas ? ` + RCAS ${sharp.toFixed(2)}` : ''}`);
        } catch (err) {
          Logger.warn('PostProcessing', `AA-first FSR failed, plain upscale-less AA: ${err}`);
        }
      }

      // 24a. TRAA (highest quality — needs velocity buffer)
      const taau = this._upscale?.mode === 'taau';
      if (!aaApplied && (config?.traa?.enabled || taau)) {
        if (!scenePassVelocity) {
          Logger.warn('PostProcessing', 'TRAA requires velocity MRT — falling back to FXAA/SMAA');
          skip('traa', this._manifest.dropped.some((d) => d.startsWith('velocity')) ? 'velocity dropped by the colour-attachment budget' : 'no velocity target');
        } else {
          try {
            const { traa } = await import('three/examples/jsm/tsl/display/TRAANode.js');
            if (this._disposed) return;
            // TAAU (2026-09-25, R3): three r185's TAAUNode — jitter in INPUT pixels shrunk to one output
            // pixel, a 9-tap Blackman-Harris reconstruction, previous depth copied at the INPUT size. The
            // TRAA stand-in it replaces jittered by output pixels and never refreshed its previous depth
            // (its copy only runs when the history equals the drawing buffer, which a low-res pass never
            // does). The HDR chain is rendered at render resolution as its beauty input, so every jittered
            // pass upstream (SSGI's temporal slices included) is resolved by it.
            let traaPass: unknown = null;
            if (taau && this._upscale) {
              try {
                const { taau: taauNode } = await import('three/examples/jsm/tsl/display/TAAUNode.js');
                const { rtt } = await import('three/tsl');
                if (this._disposed) return;
                const u = this._upscale;
                // explicit size → not auto-resized (the RTT_AUTORESIZE_LAW: the assignment threw and TRAA stood in for TAAU)
                // THE LOW-RES SIZE LAW: the beauty is EXACTLY the scene pass size — TAAU loads the pass's depth and velocity
                // on the beauty's texel grid (a rounded 985×554 over a 984×553 pass read them a row off, the last out of bounds)
                const [bw, bh] = lowResSize(u.w, u.h, u.scale);
                const t = onceAFrame(rtt(outputNode as never, bw, bh));
                this._lowResRtts.push(t as never);
                traaPass = taauNode(t as never, scenePassDepth, scenePassVelocity, this.camera);
              } catch (err) { Logger.warn('PostProcessing', `TAAUNode unavailable, TRAA stands in: ${err}`); }
            }
            if (!traaPass) traaPass = traa(outputNode as any, scenePassDepth, scenePassVelocity, this.camera);
            outputNode = traaPass;
            this._temporal = true;
            this.passes.set('traa', { name: 'traa', node: traaPass, enabled: true });
            if (taau && this._upscale && config?.upscale) {
              // TAAU: sharpen the temporal resolve (RCAS at output resolution).
              const sharp = config.upscale.sharpness ?? FSR_PRESETS[config.upscale.quality].defaultSharpness;
              if (sharp > 0) {
                // the resolve's own texture NODE (a convertToTexture would copy the output-size resolve once more)
                const resolved = (outputNode as { getTextureNode?: () => unknown }).getTextureNode?.() ?? convertToTexture(outputNode as never);
                const rcas = createRCASNode({ srcColor: resolved as never, srcWidth: this._upscale.w, srcHeight: this._upscale.h, sharpness: sharp });
                outputNode = rcas.node;
                this._upscale.rcas = rcas;
              }
              this.passes.set('upscale', { name: 'upscale', node: outputNode, enabled: true });
            }
            if ((config?.traa?.quality ?? 2) >= 8) {
              // gaps wave round 2: the late clouds join the RESOLVED frame here, before this FXAA's display transform —
              // below it they would be linear radiance on display-referred colour.
              if (cloudsLate && !cloudsComposited) {
                outputNode = compositeClouds(outputNode);
                cloudsComposited = true;
                this.passes.set('clouds', { name: 'clouds', node: outputNode, enabled: true });
                Logger.info('PostProcessing', 'Volumetric clouds pass enabled (post-resolve composite, before the TRAA FXAA)');
              }
              const { fxaa } = await import('three/examples/jsm/tsl/display/FXAANode.js');
              if (this._disposed) return;
              outputNode = fxaa(toDisplay(outputNode) as any); // FXAA wants display-referred input
            }
            aaApplied = true;
            Logger.info('PostProcessing', taau ? 'TAAU: temporal resolve over the low-resolution scene pass' : `TRAA anti-aliasing enabled (quality x${config?.traa?.quality ?? 2})`);
          } catch {
            Logger.warn('PostProcessing', 'TRAA pass not available');
          }
        }
      }
      // TEMPORAL IS A PAIR (2026-09-25): the SSGI jitter was set from the REQUEST; a request can still end with no
      // resolve (no velocity target, the temporal node unavailable) and a jitter nothing averages only shimmers.
      const ssgiBuilt = this.passes.get('ssgi')?.node as { useTemporalFiltering?: boolean } | undefined;
      if (ssgiBuilt && !this._temporal) ssgiBuilt.useTemporalFiltering = false;

      // 24b. SMAA
      if (!aaApplied && config?.smaa?.enabled) {
        try {
          // The VENDORED node: three hard-codes the paper's "1x Medium"
          // preset with no way to reach the knobs. Quality here means the
          // real thing — edge threshold and pattern search length — not
          // running the filter more than once, which is what this used to do
          // (`aaIters`) and which is not what SMAA quality means: re-filtering
          // already-filtered edges does not search further along them.
          const { smaa, SMAA_PRESETS } = await import('./vendor/SMAAQualityNode.js');
          if (this._disposed) return;
          const preset = smaaPresetFor(config.smaa.quality);
          outputNode = smaa(chainTex(outputNode), preset);
          this.passes.set('smaa', { name: 'smaa', node: outputNode, enabled: true });
          aaApplied = true;
          Logger.info('PostProcessing', `SMAA anti-aliasing enabled (${preset}: threshold ${SMAA_PRESETS[preset].threshold}, ${SMAA_PRESETS[preset].maxSearchSteps} search steps)`);
        } catch {
          Logger.warn('PostProcessing', 'SMAA pass not available');
        }
      }

      // 24c. FXAA (default)
      if (!aaApplied && config?.fxaa?.enabled !== false) {
        try {
          const { fxaa } = await import('three/examples/jsm/tsl/display/FXAANode.js');
          if (this._disposed) return;
          const n = aaIters(config?.fxaa?.quality);
          outputNode = toDisplay(outputNode); // FXAA wants display-referred input
          for (let i = 0; i < n; i++) outputNode = fxaa(outputNode as any);
          this.passes.set('fxaa', { name: 'fxaa', node: outputNode, enabled: true });
          if (n > 1) Logger.info('PostProcessing', `FXAA x${config?.fxaa?.quality ?? 2} → ${n} passes`);
        } catch {
          Logger.warn('PostProcessing', 'FXAA pass not available');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24c1. Clouds (afterAA mode) — composited on the TEMPORALLY RESOLVED
      //       frame so TRAA's jitter never smears the cloud dither or makes
      //       the depth mask flicker at geometry silhouettes.
      // ════════════════════════════════════════════════════════════════════
      if (config?.clouds?.node && config.clouds.enabled !== false && cloudsLate && !cloudsComposited) {
        // (a temporal pass that failed to build leaves the frame on SMAA / FXAA; FXAA has already tone-mapped — the
        // clouds still draw, a stop off, rather than vanish; the warning above the AA says why)
        try {
          outputNode = compositeClouds(outputNode);
          cloudsComposited = true;
          this.passes.set('clouds', { name: 'clouds', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Volumetric clouds pass enabled (post-AA composite)');
        } catch (err) {
          Logger.warn('PostProcessing', `Clouds pass (post-AA) failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24c1b. DISPLAY TRANSFORM — tone map + output colour space, ONCE, here.
      //        Everything above is linear HDR; everything below is
      //        display-referred. `outputColorTransform` is turned off at the
      //        end so three does not apply it a second time (see `toDisplay`).
      // ════════════════════════════════════════════════════════════════════
      outputNode = toDisplay(outputNode);
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24c2. Sharpen — post-AA (AA softens; the sharpen restores edge
      //       contrast), before distortion so the warp resamples the
      //       sharpened image.
      // ════════════════════════════════════════════════════════════════════
      if (config?.sharpen && config.sharpen.enabled !== false) {
        try {
          const sh = createSharpenNode(outputNode, config.sharpen);
          outputNode = sh.node;
          this._sharpenUniforms = sh.uniforms;
          this.passes.set('sharpen', { name: 'sharpen', node: sh.node, enabled: true });
          Logger.info('PostProcessing', 'Sharpen pass enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Sharpen failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24d. Lens distortion — AFTER AA (it warps by resampling; distorting
      //      first would alias the warped edges), then vignette on top.
      // ════════════════════════════════════════════════════════════════════
      if (config?.lensDistortion && config.lensDistortion.enabled !== false) {
        try {
          const ld = createLensDistortionNode(outputNode, config.lensDistortion);
          outputNode = ld.node;
          this._lensDistortionUniforms = ld.uniforms;
          this.passes.set('lensDistortion', { name: 'lensDistortion', node: ld.node, enabled: true });
          Logger.info('PostProcessing', 'Lens distortion pass enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Lens distortion failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24d1. Colour grade + 3D LUT — DISPLAY-referred (lighting agent,
      //       2026-09-25, Atlas realism §10 R2): after the tone map, where a
      //       LUT's [0,1] domain is the picture (grade → LUT, the conventional
      //       order). Moved from the HDR chain, where the lookup clamped every
      //       highlight above 1 to the cube's corner colour.
      // ════════════════════════════════════════════════════════════════════
      if (config?.colorGrade && config.colorGrade.enabled !== false) {
        try {
          const grade = createColorGradeNode(outputNode, config.colorGrade);
          outputNode = grade.node;
          this._colorGradeUniforms = grade.uniforms;
          this.passes.set('colorGrade', { name: 'colorGrade', node: grade.node, enabled: true });
          Logger.info('PostProcessing', 'Parametric colour grade enabled (display-referred)');
        } catch (err) {
          Logger.warn('PostProcessing', `Colour grade failed: ${err}`);
        }
      }
      if (this._disposed) return;
      if (config?.lut3D) {
        try {
          const { lut3D } = await import('three/examples/jsm/tsl/display/Lut3DNode.js');
          if (this._disposed) return;
          const lutPass = (lut3D as any)(
            outputNode,
            texture3D(config.lut3D.lut as never), // a texture NODE — lut3D calls .sample() on it (a raw texture broke: 'lutNode.sample is not a function')
            config.lut3D.size,
            config.lut3D.intensity ?? 1,
          );
          outputNode = lutPass;
          this.passes.set('lut3D', { name: 'lut3D', node: lutPass, enabled: true });
          Logger.info('PostProcessing', 'LUT 3D pass enabled (display-referred)');
        } catch {
          Logger.warn('PostProcessing', 'LUT 3D pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24d2. Chromatic aberration — a LENS effect, after AA and the upscale
      //       (AMD's order, §10 R11): aberrating first made the AA smear the
      //       fringes and the upscaler resample them.
      // ════════════════════════════════════════════════════════════════════
      if (config?.chromaticAberration) {
        try {
          const { chromaticAberration } = await import('three/examples/jsm/tsl/display/ChromaticAberrationNode.js');
          if (this._disposed) return;
          // WHY an explicit center: three r185's factory does nodeObject(center)
          // and nodeObject(null) === null — the documented "null = screen
          // center" default is NOT implemented, so a null reached the shader
          // Fn and produced an invalid program: a silent black frame on
          // WebGPU with no JS error (field-hit 2026-08-20).
          const caPass = (chromaticAberration as any)(
            outputNode,
            config.chromaticAberration.strength ?? 1.0,
            vec2(0.5, 0.5), // screen center
            config.chromaticAberration.scale ?? 1.1,
          );
          outputNode = caPass;
          this.passes.set('chromaticAberration', { name: 'chromaticAberration', node: caPass, enabled: true });
          Logger.info('PostProcessing', 'Chromatic aberration pass enabled (after AA/upscale)');
        } catch {
          Logger.warn('PostProcessing', 'Chromatic aberration pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24e. Vignette — display-referred radial darkening.
      // ════════════════════════════════════════════════════════════════════
      if (config?.vignette && config.vignette.enabled !== false) {
        try {
          const vg = createVignetteNode(outputNode, config.vignette);
          outputNode = vg.node;
          this._vignetteUniforms = vg.uniforms;
          this.passes.set('vignette', { name: 'vignette', node: vg.node, enabled: true });
          Logger.info('PostProcessing', 'Vignette pass enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Vignette failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24f. Film grain, 'luma' mode (lighting agent 2026-09-25, §10 R11):
      //      zero-mean, luminance-weighted (a film's grain scales with
      //      exposure), plus a ±½-LSB dither against 8-bit banding — AFTER AA
      //      and the upscale, on display values (grain before AA was smeared
      //      and then resampled, reading as noise crawl, not grain).
      // ════════════════════════════════════════════════════════════════════
      if (config?.film && config.film.mode === 'luma') {
        try {
          const node = createLumaGrainNode(outputNode, config.film.intensity ?? 0.015);
          outputNode = node;
          this.passes.set('film', { name: 'film', node, enabled: true });
          Logger.info('PostProcessing', `Film grain (luma, ${config.film.intensity ?? 0.015}) after AA/upscale`);
        } catch (err) {
          Logger.warn('PostProcessing', `Film grain failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 25. Accessibility colourblind correction — FINAL display-referred step.
      //     Applied after tone-map + AA so it operates on the same colours the
      //     user sees. No-op unless an explicit transform is supplied.
      // ════════════════════════════════════════════════════════════════════
      if (config?.colorblind?.transform && config.colorblind.enabled !== false) {
        try {
          outputNode = config.colorblind.transform(outputNode);
          this.passes.set('colorblind', { name: 'colorblind', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Colourblind correction pass enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Colourblind pass failed: ${err}`);
        }
      }
      if (this._disposed) return;

      this.postProcessing.outputNode = outputNode as any;
      // The chain has already tone-mapped and encoded at the boundary above;
      // leaving three's own transform on would apply it a SECOND time.
      (this.postProcessing as unknown as { outputColorTransform?: boolean }).outputColorTransform = !displayTransformed;
      this._ready = true;

      Logger.info('PostProcessing', `Pipeline created with ${this.passes.size} passes`);
    } catch (err) {
      Logger.warn('PostProcessing', `Failed to create pipeline: ${err}`);
      this.postProcessing = null;
      this._ready = false;
    }
  }

  /**
   * Add a custom pass node.
   */
  addPass(name: string, node: unknown): void {
    this.passes.set(name, { name, node, enabled: true });
  }

  /**
   * Remove a pass by name.
   */
  removePass(name: string): boolean {
    return this.passes.delete(name);
  }

  /**
   * Enable or disable a pass.
   * Note: TSL-based post-processing chains are statically compiled.
   * This flag is tracked for external consumers (e.g. QualityManager)
   * but does NOT dynamically rebuild the pipeline. A full pipeline
   * rebuild is needed to actually add/remove passes from the chain.
   */
  setEnabled(name: string, enabled: boolean): void {
    const entry = this.passes.get(name);
    if (entry) entry.enabled = enabled;
  }

  /**
   * Check if a pass is enabled.
   */
  isEnabled(name: string): boolean {
    return this.passes.get(name)?.enabled ?? false;
  }

  /**
   * Get/set overall pipeline enabled state.
   */
  get enabled(): boolean {
    return this._enabled;
  }

  set enabled(value: boolean) {
    this._enabled = value;
  }

  /**
   * Update the camera reference (e.g., when switching cameras).
   */
  setCamera(camera: THREE.Camera): void {
    this.camera = camera;
    // Also update the scene pass node's internal camera reference.
    // Without this, the post-processing pipeline continues rendering
    // with the old camera even though this.camera changed.
    if (this._scenePass) {
      this._scenePass.camera = camera;
    }
    // Refresh DOF near/far uniforms so the linearization tracks the new
    // camera. (Both PerspectiveCamera and OrthographicCamera expose `near`
    // and `far` as numeric properties.)
    const cam = camera as { near?: number; far?: number };
    if (this._dofNearUniform && typeof cam.near === 'number') {
      this._dofNearUniform.value = cam.near;
    }
    if (this._dofFarUniform && typeof cam.far === 'number') {
      this._dofFarUniform.value = cam.far;
    }
    // Refresh aerial-perspective camera uniforms (same rationale as DoF).
    if (this._aerialUniforms) {
      const p = camera as { near?: number; far?: number; fov?: number; aspect?: number };
      if (typeof p.near === 'number') this._aerialUniforms.near.value = p.near;
      if (typeof p.far === 'number') this._aerialUniforms.far.value = p.far;
      if (typeof p.fov === 'number') this._aerialUniforms.tanHalfFovY.value = Math.tan((p.fov * Math.PI) / 360);
      if (typeof p.aspect === 'number') this._aerialUniforms.aspect.value = p.aspect;
    }
    if (this._volumetricFogUniforms) {
      const p = camera as { near?: number; far?: number; fov?: number; aspect?: number };
      if (typeof p.near === 'number') this._volumetricFogUniforms.near.value = p.near;
      if (typeof p.far === 'number') this._volumetricFogUniforms.far.value = p.far;
      if (typeof p.fov === 'number') this._volumetricFogUniforms.tanHalfFovY.value = Math.tan((p.fov * Math.PI) / 360);
      if (typeof p.aspect === 'number') this._volumetricFogUniforms.aspect.value = p.aspect;
    }
    if (this._capsuleShadowsUniforms) {
      const p = camera as { near?: number; far?: number; fov?: number; aspect?: number };
      if (typeof p.near === 'number') this._capsuleShadowsUniforms.near.value = p.near;
      if (typeof p.far === 'number') this._capsuleShadowsUniforms.far.value = p.far;
      if (typeof p.fov === 'number') this._capsuleShadowsUniforms.tanHalfFovY.value = Math.tan((p.fov * Math.PI) / 360);
      if (typeof p.aspect === 'number') this._capsuleShadowsUniforms.aspect.value = p.aspect;
    }
  }

  /**
   * Render the post-processing pipeline.
   */
  render(): void {
    if (!this._enabled || !this._ready || !this.postProcessing) {
      // Fallback to raw render until pipeline is fully configured
      this.renderer.render(this.scene, this.camera);
      return;
    }

    // WHY refresh here: DOF depth linearization reads camera near/far. These
    // were only synced in setCamera(), so an in-place mutation of
    // camera.near/far (without re-calling setCamera) left the DOF uniforms
    // stale — contradicting the per-frame-refresh comment on the fields. This
    // is a couple of scalar writes, cheap enough to do every frame.
    const cam = this.camera as { near?: number; far?: number };
    if (this._dofNearUniform && typeof cam.near === 'number') {
      this._dofNearUniform.value = cam.near;
    }
    if (this._dofFarUniform && typeof cam.far === 'number') {
      this._dofFarUniform.value = cam.far;
    }
    this._refreshContactShadowUniforms();
    this._refreshUpscaleSizes();
    this._refreshCloudRtt();
    this._refreshLensFlareTexel();
    this._refreshKawase();
    this._tickHistoryHold();

    this.postProcessing.render();
  }

  /** Keep the EASU/RCAS size uniforms in step with the drawing buffer (a resize
   *  or a live render-scale change); a couple of vec2 writes when unchanged. */
  private _refreshUpscaleSizes(): void {
    const u = this._upscale;
    if (!u) return;
    const size = this.renderer.getDrawingBufferSize(_upscaleSize);
    const w = Math.max(1, Math.round(size.x)), h = Math.max(1, Math.round(size.y));
    if (w === u.w && h === u.h) return;
    u.w = w; u.h = h;
    const [sw, sh] = lowResSize(w, h, u.scale); // THE LOW-RES SIZE LAW
    if (u.easu) {
      (u.easu.uniforms.srcSize as { value: THREE.Vector2 }).value.set(sw, sh);
      (u.easu.uniforms.dstSize as { value: THREE.Vector2 }).value.set(w, h);
    }
    if (u.rcas) (u.rcas.uniforms.invSrcSize as { value: THREE.Vector2 }).value.set(1 / w, 1 / h);
    for (const t of this._lowResRtts) { try { t.setSize?.(sw, sh); } catch { /* best-effort */ } }
  }

  /** True on the WebGPU (Dawn) backend, false on the WebGL2 fallback (and on a mocked renderer without a backend). */
  private _isWebGPU(): boolean {
    const b = (this.renderer as unknown as { backend?: { isWebGPUBackend?: boolean } }).backend;
    return b?.isWebGPUBackend === true;
  }

  /** TAAU needs the velocity MRT, which the WebGL2 backend cannot mix with non-MRT draws. */
  private _taauSupported(): boolean {
    const b = (this.renderer as unknown as { backend?: { isWebGPUBackend?: boolean } }).backend;
    return b?.isWebGPUBackend === true;
  }

  /** Release the render-resolution RTTs (an RTTNode owns a RenderTarget and has no dispose of its own). */
  private _disposeLowResRtts(): void {
    for (const t of this._lowResRtts) { try { t.renderTarget?.dispose?.(); } catch { /* best-effort */ } }
    this._lowResRtts = [];
  }

  /** Release THE ONE EVALUATION's chain RTTs (same reason: no dispose of their own — every rebuild leaked them). */
  private _disposeChainRtts(): void {
    for (const t of this._chainRtts) { try { t.renderTarget?.dispose?.(); } catch { /* best-effort */ } }
    for (const t of this._outRtts) { try { t.renderTarget?.dispose?.(); } catch { /* best-effort */ } }
    this._chainRtts = [];
    this._outRtts = [];
  }

  /** 'off' | 'fsr' | 'taau' — the upscale this build runs (mip-bias and camera consumers read it). */
  get upscaleMode(): 'off' | 'fsr' | 'taau' { return this._upscale?.mode ?? 'off'; }
  /** True when this build resolves temporally (TRAA at native, or TAAU). */
  get temporalAA(): boolean { return this._temporal; }

  /** The drawing buffer size, tolerant of renderers without the query (a 1080p fallback). */
  private _drawingBufferSize(): { x: number; y: number } {
    const r = this.renderer as { getDrawingBufferSize?: (t: THREE.Vector2) => THREE.Vector2 };
    return typeof r.getDrawingBufferSize === 'function' ? r.getDrawingBufferSize(_upscaleSize) : { x: 1920, y: 1080 };
  }

  /** Keep the lens-flare texel size in step with the drawing buffer. */
  /** Dual-Kawase bloom (stage 4): the level texel sizes + RTT sizes follow the drawing buffer. */
  private _kawaseSize = { w: 0, h: 0 };
  private _refreshKawase(): void {
    if (!this._kawase) return;
    const size = this._drawingBufferSize();
    if (size.x === this._kawaseSize.w && size.y === this._kawaseSize.h) return;
    this._kawaseSize = { w: size.x, h: size.y };
    try { this._kawase.setSize(size.x, size.y); } catch { /* best-effort */ }
  }

  private _refreshLensFlareTexel(): void {
    const lf = this._lensFlare as { uniforms?: { texelSize?: { value?: { x: number; y: number; set?: (x: number, y: number) => void } } } } | null;
    const t = lf?.uniforms?.texelSize?.value;
    if (!t) return; // three's LensflareNode sizes itself
    const size = this._drawingBufferSize();
    const tx = 1 / Math.max(1, size.x), ty = 1 / Math.max(1, size.y);
    if (Math.abs(t.x - tx) > 1e-9 || Math.abs(t.y - ty) > 1e-9) { if (t.set) t.set(tx, ty); else { t.x = tx; t.y = ty; } }
  }

  /**
   * Release the fractional-resolution cloud RTT. WHY: `rtt()` is an RTTNode
   * owning a RenderTarget (rs × the drawing buffer) and has no dispose() of
   * its own; it lives only in `_cloudRtt`, outside `passes`, so every tier
   * rebuild / world transition leaked one RGBA target (2026-09-12).
   */
  private _disposeCloudRtt(): void {
    const c = this._cloudRtt;
    if (!c) return;
    this._cloudRtt = null;
    const rt = (c.node as { renderTarget?: { dispose?: () => void } }).renderTarget;
    try { rt?.dispose?.(); } catch (err) { Logger.warn('PostProcessing', `cloud RTT dispose failed: ${err}`); }
  }

  /** Keep the cloud RTT at rs × the drawing buffer across resizes. */
  private _refreshCloudRtt(): void {
    const c = this._cloudRtt;
    if (!c) return;
    const size = this.renderer.getDrawingBufferSize(_upscaleSize);
    if (size.x === c.w && size.y === c.h) return;
    c.w = size.x; c.h = size.y;
    c.node.setSize?.(Math.max(1, Math.round(size.x * c.rs)), Math.max(1, Math.round(size.y * c.rs)));
  }

  /**
   * Move the depth of field LIVE (R4: third-person autofocus, the cinematic dialogue lens, first person =
   * bokeh 0). Values left undefined keep theirs. False when no standard DOF pass is built.
   */
  setDof(p: { focusDistance?: number; focusRange?: number; bokehScale?: number }): boolean {
    if (!this._dofFocus) return false;
    if (p.focusDistance !== undefined && Number.isFinite(p.focusDistance)) this._dofFocus.value = Math.max(0.05, p.focusDistance);
    if (p.focusRange !== undefined && Number.isFinite(p.focusRange)) this._dofRange.value = Math.max(0.05, p.focusRange);
    // in OUTPUT pixels — the uniform carries it in the DOF input's pixels (× _dofBokehK, THE ONE EVALUATION)
    if (p.bokehScale !== undefined && Number.isFinite(p.bokehScale)) this._dofBokeh.value = Math.max(0, p.bokehScale) * this._dofBokehK;
    return true;
  }
  /** The live DOF inputs (null when no standard DOF pass is built); `bokehScale` in OUTPUT pixels, as `setDof` takes it. */
  get dofState(): { focusDistance: number; focusRange: number; bokehScale: number } | null {
    return this._dofFocus ? { focusDistance: this._dofFocus.value, focusRange: this._dofRange.value, bokehScale: this._dofBokeh.value / (this._dofBokehK || 1) } : null;
  }

  /** The current upscale ratio (output / render), 1 when no upscale is built. */
  get renderScale(): number { return this._upscale?.scale ?? 1; }

  /**
   * Move the render scale LIVE (a frame-time governor's lever): the scene pass
   * re-targets to output/scale on its next render and the EASU kernel follows.
   * No pipeline rebuild. Only when an upscale is built; clamped to [1, 2.5].
   */
  setRenderScale(scale: number): boolean {
    const u = this._upscale;
    if (!u || !this._scenePass) return false;
    const s2 = Math.max(1, Math.min(2.5, scale));
    if (Math.abs(s2 - u.scale) < 1e-4) return true;
    u.scale = s2;
    (this._scenePass as { setResolutionScale?: (s: number) => void }).setResolutionScale?.(1 / s2);
    if (this._ao) this._ao.node.resolutionScale = this._ao.base / s2; // THE AO-GRID LAW: the AO grid follows the pass
    if (this._ssr) this._ssr.node.resolutionScale = this._ssr.base / s2; // …and so does SSR's (2026-09-27)
    // THE ONE EVALUATION: a chain consumed at render resolution follows the pass too (and the DOF's bokeh with it).
    if (this._chainAtRender) {
      const k = 1 / s2;
      for (const t of this._chainRtts) { try { t.setResolutionScale?.(k); } catch { /* best-effort */ } }
      if (this._dofBokeh && this._dofBokehK !== 1) { this._dofBokeh.value = (this._dofBokeh.value / this._dofBokehK) * k; this._dofBokehK = k; }
      this._chainScale = k;
    }
    u.w = -1; // force the size refresh
    this._refreshUpscaleSizes();
    return true;
  }

  /**
   * THE HISTORY EPOCH (shader gaps program, 2026-09-29 — the report's §4.3 / §14.6). A camera cut, a teleport, a world
   * or sector swap makes every temporal history describe a picture that no longer exists: TAAU / TRAA would smear the
   * old view into the new one for a dozen frames and the velocity of the first new frame is the jump itself. three's
   * TAAUNode / TRAANode re-seed their history from the current beauty ONLY when the history size differs from the
   * output (`needsRestart`), and expose no reset — so a reset zeroes the history target's recorded width, which sends
   * the next frame down exactly that path (a one-frame re-allocation). The motion blur is held off for two frames (the
   * cut's own velocity would streak the whole screen). A pure origin rebase is NOT a cut: it moves the camera and
   * every object together, the velocities stay consistent — do not call this for one. Returns the new epoch.
   */
  resetHistory(reason = 'cut'): number {
    this._historyEpoch++;
    this._manifest.lastReset = reason;
    const t = this.passes.get('traa')?.node as { _historyRenderTarget?: { width: number } } | undefined;
    if (t?._historyRenderTarget) t._historyRenderTarget.width = 0; // → needsRestart on the next frame: re-seeded from the beauty
    const mb = this.passes.get('motionBlur')?.node as { intensity?: { value: number } } | undefined;
    if (mb?.intensity) {
      if (this._mbIntensityHeld === null) this._mbIntensityHeld = mb.intensity.value;
      mb.intensity.value = 0;
      this._historyHold = 2;
    }
    return this._historyEpoch;
  }

  /** The epoch `resetHistory` last returned (0 before any reset). */
  get historyEpoch(): number { return this._historyEpoch; }

  /** Count down the post-reset hold (called once a rendered frame). */
  private _tickHistoryHold(): void {
    if (this._historyHold <= 0 || --this._historyHold > 0) return;
    const mb = this.passes.get('motionBlur')?.node as { intensity?: { value: number } } | undefined;
    // restore only what the hold zeroed (a menu write during the hold wins)
    if (mb?.intensity && mb.intensity.value === 0 && this._mbIntensityHeld !== null) mb.intensity.value = this._mbIntensityHeld;
    this._mbIntensityHeld = null;
  }

  /** THE BUILT-PASS MANIFEST — see `PassManifest`. */
  passManifest(): PassManifest {
    const names = Array.from(this.passes.keys());
    const boundary = names.indexOf('outputTransform');
    const built = names.map((name, i) => ({ name, domain: (boundary >= 0 && i >= boundary ? 'display' : 'hdr') as 'hdr' | 'display' }));
    const skipped = this._manifest.requested
      .filter((n) => !this.passes.has(n))
      .map((name) => ({ name, reason: this._manifest.skips.get(name) ?? (this._ready ? 'not built — see the PostProcessing log' : 'setup pending') }));
    const rt = this.scenePassRenderTarget;
    const db = this._drawingBufferSize();
    const temporal: PassManifest['temporal'] = !this._temporal ? 'none' : this._upscale?.mode === 'taau' ? 'taau' : 'traa';
    return {
      backend: this._isWebGPU() ? 'webgpu' : 'webgl2',
      attachments: [...this._manifest.attachments],
      attachmentBytes: 8 * this._manifest.attachments.length,
      attachmentCap: this._manifest.attachmentCap,
      dropped: [...this._manifest.dropped],
      requested: [...this._manifest.requested],
      built, skipped, temporal,
      historyEpoch: this._historyEpoch,
      lastReset: this._manifest.lastReset,
      sceneSize: rt ? [rt.width, rt.height] : null,
      outputSize: [Math.round(db.x), Math.round(db.y)],
      upscale: this._upscale ? { mode: this._upscale.mode, ratio: this._upscale.scale } : null,
      chain: { textures: this._chainLegacy ? 'legacy' : 'shared', scale: this._chainScale, count: this._chainRtts.length },
    };
  }

  /**
   * List pass names.
   */
  getPassNames(): string[] {
    return Array.from(this.passes.keys());
  }

  dispose(): void {
    this._disposed = true;
    this._ready = false;
    // WHY: three r185 RenderPipeline.dispose() frees only its fullscreen-quad
    // material. The scene PassNode's (MRT) render target and every display
    // node's internal targets — bloom mip chain, GTAO/SSGI/SSR/denoise, TRAA
    // history, DoF, blur, transition's second scene pass — are released only
    // by their OWN dispose(), which nothing called: every pipeline teardown /
    // quality-tier rebuild leaked a full-resolution HDR target set. A plain
    // TSL Node.dispose() just dispatches its 'dispose' event (no renderer
    // listeners), so calling it on every registered pass is safe.
    for (const entry of this.passes.values()) {
      this._disposeNode(entry.node, entry.name);
    }
    for (const owned of this._ownedPassNodes) this._disposeNode(owned, 'pass');
    this._ownedPassNodes.length = 0;
    this._disposeCloudRtt();
    this._disposeLowResRtts();
    this._disposeChainRtts();
    try { this._kawase?.dispose?.(); } catch { /* best-effort */ }
    this._kawase = null;
    this._disposeNode(this._scenePass, 'scene');
    this.postProcessing?.dispose();
    this.postProcessing = null;
    this.passes.clear();
    this._godRays = null;
    this._lensFlare = null;
    this._contactShadowsUniforms = null;
    this._ao = null;
    this._ssr = null;
    this._scenePass = null;
  }

  /** Best-effort node teardown — a throwing dispose must not abort the rest. */
  private _disposeNode(node: unknown, label: string): void {
    const d = node as { dispose?: () => void } | null | undefined;
    if (!d || typeof d.dispose !== 'function') return;
    try {
      d.dispose();
    } catch (err) {
      Logger.debug('PostProcessing', `Pass "${label}" dispose failed: ${err}`);
    }
  }
}
